<template>
  <div>
    <v-card height="200" width="100%" elevation="0" v-if="fetchingProducts">
      <div class="d-flex justify-center align-center ma-5">
        <b class="mr-3">Loading Cart...</b>
        <v-progress-circular :indeterminate="true" />
      </div>
    </v-card>
    <v-card
      v-if="storeUnavailable && !campaign"
      width="100%"
      elevation="0"
      data-testid="cart-store-unavailable"
    >
      <div class="d-flex justify-center align-center ma-5 text-center">
        This store's cart could not be loaded. Please refresh the page to
        try again.
      </div>
    </v-card>
    <v-container class="checkout-margin" v-if="campaign">
      <div class="keep-shopping" @click="keepShopping" data-testid="co-back">
        Keep Shopping
      </div>
      <!-- Live-rate shipping card: the store rates live but has no shipping
           origin, so no rate can exist and checkout refuses the order. A
           plain element (not a Vuetify alert) so the message is always in
           the DOM the ui-runner and the unit test read. -->
      <div
        v-if="shippingOriginMissing"
        role="alert"
        class="shipping-origin-missing pa-4 mt-3 mb-2"
        data-testid="cart-shipping-origin-missing"
      >
        {{ shippingOriginErrorMessage }}
      </div>
      <v-row justify="center" align="start">
        <v-col cols="12" class="mt-5 mb-5 product-spinner d-flex justify-center" v-if="isCheckoutProcess">
          <v-progress-circular
            :indeterminate="true"
            color="secondary"
            size="100"
            width="12"
          />
        </v-col>

        <!-- Bug Intake #637: a Place Order whose response was lost is
             CONFIRMED before anything is said. Plain elements so the message
             is always in the DOM the ui-runner and the unit test read. -->
        <v-col cols="12" v-if="confirmingOrder">
          <div
            role="status"
            class="text-center pa-4"
            data-testid="cart-confirming-order"
          >
            Confirming your order...
          </div>
        </v-col>
        <v-col cols="12" v-if="orderUnconfirmed && !confirmingOrder">
          <div
            role="alert"
            class="text-center pa-4"
            data-testid="cart-order-unconfirmed"
          >
            We could not confirm your order yet. Please do not place it again:
            check your email or your order history in a few minutes. If you
            press Place Order, the order you already placed is the one you will
            see.
          </div>
        </v-col>

        <!-- Left column: Checkout -->
        <v-col cols="12" md="6">
          <cart-checkout
            ref="checkout"
            :campaign="campaign"
            :checkout-fields-result="checkoutFieldsResult"
            :custom-checkout-fields-result="customCheckoutFieldsResult"
            :custom-checkout-fields="customCheckoutFields"
            :checkout-custom-dropdowns="checkoutCustomDropdowns"
            :coupon="coupon"
            :coupon-amount="couponAmount"
            :coupon-type="couponType"
            :is-payroll="isPayroll"
            :is-card="isCard"
            :is-email-confirmed="isEmailConfirmed"
            :panel1="panel1"
            :panel2="panel2"
            :cc-dialog="ccDialog"
            :create-pending-order="createPendingOrder"
            :is-checkout-process="isCheckoutProcess"
            :sales-tax-loading="salesTaxLoading"
            :sales-tax-state="salesTaxStateShown"
            :calculating-shipping="calculatingShipping"
            :place-order-button-disabled="placeOrderBlocked"
            :coupon-checking="couponChecking"
            :ship-to-shopper-address="shipToShopperAddress"
            :shopper-location="shopperLocation"
            :countries-and-codes="countriesAndCodes"
            :store-ship-to="storeShipTo"
            :store-ship-to-state="storeShipToState"
            :total="total"
            :cart-total="cartTotal"
            :sub-total="subTotal"
            :cart-charge="cartCharge"
            :server-totals="serverTotals"
            :computed-sales-tax="computedSalesTax"
            :shipping-cost="shippingCost"
            :company-allowance="companyAllowance"
            :free-item-discount="freeItemDiscount"
            :cart-processing-fee="cartProcessingFee"
            :app-fee="appFee"
            :platform-fee="platformFeeAmount"
            :shopper-bears-platform-fee="shopperBearsPlatformFee"
            :cart-sales-tax="cartSalesTax"
            :collect-sales-tax="collectSalesTax"
            :consolidate-tax-fee="consolidateTaxFee"
            :collect-processing-fee="collectProcessingFee"
            :show-min-charge-text="showMinChargeText"
            :is-sales-tax-t-b-d="salesTaxTBDShown"
            :is-cart-processing-fee-t-b-d="isCartProcessingFeeTBD"
            :is-shipping-cost-t-b-d="isShippingCostTBD"
            :cart="cart"
            :stripe="stripe"
            :setup-stripe="setupStripe"
            :stripe-remount-key="stripeRemountKey"
            @update:coupon="coupon = $event"
            @update:coupon-amount="couponAmount = $event"
            @update:cc-dialog="ccDialog = $event"
            @update:is-email-confirmed="isEmailConfirmed = $event"
            @update:panel1="panel1 = $event"
            @update:panel2="panel2 = $event"
            @update:ship-to-shopper-address="shipToShopperAddress = $event"
            @update:is-cart-processing-fee-t-b-d="isCartProcessingFeeTBD = $event"
            @update:costom-dropdowns-valid="costomDropdownsValid = $event"
            @email-confirmed="handleEmailConfirmed"
            @email-verified="handleEmailVerified"
            @location-changed="handleLocationChanged"
            @form-valid-changed="shopperLocationFormValid = $event"
            @place-order-clicked="placeOrderButtonClick"
            @confirm-order="confirmPlaceOrder"
            @apply-coupon="applyCoupon"
            @payment-complete="handlePaymentComplete"
            @payment-failed="handlePaymentFailed"
          />
        </v-col>

        <!-- Right column: Cart & Summary -->
        <v-col cols="12" md="6" class="checkout">
          <v-card variant="flat" class="pa-2">
            <div class="mb-3 panel-header">Cart & Order Summary</div>
            <v-divider />
            <v-card variant="flat">
              <cart-item-list
                ref="cartItems"
                :cart="cart"
                :campaign="campaign"
                :calculating-shipping="calculatingShipping"
                :sales-tax-loading="salesTaxLoading"
                :fundraising-amount-per-item="fundraisingAmountPerItem"
                :fundraising-type="fundraisingType"
                @cart-updated="handleCartUpdated"
              />
              <!-- No lines of THIS store. Only once a load has finished
                   ("loaded"), so it never flashes while the cart loads, and
                   only while the store is loaded: after a failed store read
                   the page cannot say what the cart holds. -->
              <div
                v-if="
                  campaign &&
                  !storeUnavailable &&
                  cart.length === 0 &&
                  linePricesState === 'loaded'
                "
                class="pa-4 text-medium-emphasis"
                data-testid="cart-empty"
              >
                Your cart is empty.
              </div>
              <!--
                Server line prices (cart_prices). While they load, or if they
                could not be loaded, the summary is withheld: totals built from
                unpriced lines would read $0.00, a number this cart does not
                have. Checkout is disabled in both states
                (placeOrderButtonDisabled).
              -->
              <!-- "idle" too: before the first load resolves there is no
                   price, and rendering the summary then flashed
                   "Subtotal: $0.00" on every load (8089, 2026-09-29). -->
              <!-- Only before the FIRST priced load. A later re-price (a
                   quantity step) keeps the last totals on screen, dimmed and
                   marked busy, below (#3884 gate P2-5). -->
              <div
                v-if="
                  (linePricesState === 'loading' || linePricesState === 'idle') &&
                  !pricesLoadedOnce
                "
                class="pa-4 text-medium-emphasis"
                data-testid="cart-prices-loading"
              >
                Loading prices...
              </div>
              <v-alert
                v-else-if="linePricesState === 'error'"
                type="error"
                variant="tonal"
                class="ma-2"
                data-testid="cart-prices-error"
              >
                We couldn't load prices for your cart, so checkout is
                unavailable right now.
                <div
                  v-if="linePricesError"
                  class="mt-1"
                  data-testid="cart-prices-error-detail"
                >{{ linePricesError }}</div>
                <v-btn
                  variant="text"
                  class="ml-2"
                  data-testid="cart-prices-retry"
                  @click="retryLinePrices"
                  >Retry</v-btn
                >
              </v-alert>
              <template v-else>
                <div
                  v-if="repricing"
                  class="px-4 pt-2 text-medium-emphasis"
                  data-testid="cart-prices-updating"
                >
                  Updating prices...
                </div>
                <cart-summary
                  :class="{ 'cart-summary--repricing': repricing }"
                  :aria-busy="repricing ? 'true' : null"
                  :campaign="campaign"
                  :total="total"
                  :sub-total="subTotal"
                  :cart-charge="cartCharge"
                  :computed-sales-tax="computedSalesTax"
                  :collect-sales-tax="collectSalesTax"
                  :consolidate-tax-fee="consolidateTaxFee"
                  :collect-processing-fee="collectProcessingFee"
                  :coupon-amount="couponAmount"
                  :coupon-type="couponType"
                  :coupon="coupon"
                  :shipping-cost="shippingCost"
                  :company-allowance="companyAllowance"
                  :free-item-discount="freeItemDiscount"
                  :cart-processing-fee="cartProcessingFee"
                  :app-fee="appFee"
                  :platform-fee="platformFeeAmount"
                  :shopper-bears-platform-fee="shopperBearsPlatformFee"
                  :server-totals="serverTotals"
                  :is-sales-tax-t-b-d="salesTaxTBDShown"
                  :is-shipping-cost-t-b-d="isShippingCostTBD"
                  :is-cart-processing-fee-t-b-d="isCartProcessingFeeTBD"
                  :sales-tax-loading="salesTaxLoading"
                  :sales-tax-state="salesTaxStateShown"
                  :calculating-shipping="calculatingShipping"
                  :show-min-charge-text="showMinChargeText"
                  :ship-to-shopper-address="shipToShopperAddress"
                />
              </template>
            </v-card>
          </v-card>
        </v-col>

        <!-- Checkout staleness dialog (admin edited campaign mid-checkout) -->
        <v-dialog
          v-model="showStalenessDialog"
          max-width="600"
          persistent
        >
          <v-card>
            <v-card-title class="text-h5">{{
              stalenessDialogTitle
            }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text data-testid="staleness-dialog-body">{{
              stalenessDialogBody
            }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                variant="flat"
                data-testid="staleness-dialog-ok"
                @click="showStalenessDialog = false"
                >OK, Review Changes</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Orphan-charge dialog (Stripe charged but checkout failed) -->
        <!-- Gate review P1 #3 — interim. Persistent so the shopper -->
        <!-- can't dismiss into a confused navigation; the support  -->
        <!-- reference (paymentIntent.id) must be visible.          -->
        <v-dialog
          v-model="showOrphanChargeDialog"
          max-width="600"
          persistent
        >
          <v-card>
            <v-card-title class="text-h5">Payment Received — Order Could Not Be Saved</v-card-title>
            <v-divider></v-divider>
            <v-card-text data-testid="orphan-charge-dialog-body">
              <p>
                Your card was charged but we were unable to save your
                order due to a problem on our end. Please contact
                support to resolve this — they will be able to either
                complete your order or refund the charge.
              </p>
              <p class="mt-3">
                <strong>Reference:</strong>
                <span data-testid="orphan-charge-payment-intent-id">{{
                  orphanChargePaymentIntentId
                }}</span>
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                variant="flat"
                data-testid="orphan-charge-dialog-ok"
                @click="showOrphanChargeDialog = false"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Notification dialog (shared across children) -->
        <v-dialog v-model="showNotificationDialog" max-width="600">
          <v-card>
            <v-card-title class="text-h5">{{
              notificationHeader
            }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text data-testid="valid-coupon-dialog">{{
              notificationBody
            }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                data-testid="close-valid-coupon-dialog"
                color="primary"
                variant="text"
                @click="showNotificationDialog = false"
                >{{ notificationBtn }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>

      <!-- Address validation dialog -->
      <validate-address-dialog
        ref="validateAddressDialog"
        :originalLocation="shopperLocation"
        @useValidatedAddress="updateAddressToValidated"
        @useOriginalAddress="doCompleteOrderWork"
        @verificationUnavailable="onAddressVerificationUnavailable"
        @addressUnmatched="onAddressVerificationUnavailable"
      />

      <!-- Footer.

           WHITE-LABEL (card https://trello.com/c/mq26K91V, Unit 3): the
           cart does NOT mount StorefrontFooter -- it carries this inline
           copy, which predates that component. So fixing the shared
           footer does nothing here, and without this branch a shopper on
           a white-labeled store would see the distributor's brand on
           every product page and ThreadFlow's at the moment they pay.

           The rule is `@/utils/whiteLabelMark`, shared with
           StorefrontFooter. `attribution` is bound as
           TEXT, never v-html, and never as an href. -->
      <v-footer class="justify-center mt-2 pa-2">
        <span
          v-if="whiteLabelMark.isLink"
          class="sf-cart-brand"
          data-testid="storefront-cart-brand-link-wrap"
        >
          <WhiteLabelMark
            :mark="whiteLabelMark"
            testid="storefront-cart-brand-link"
            logo-testid="storefront-cart-brand-logo"
          />
        </span>
        <WhiteLabelMark
          v-else
          :mark="whiteLabelMark"
          testid="storefront-cart-brand"
          logo-testid="storefront-cart-brand-logo"
        />
        <v-spacer />
      </v-footer>
    </v-container>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import {
  isUnparseableAddress,
  unparseableAddressMessage,
} from "@/utils/addressParse";
import {
  FLAT_SHIPPING_TYPES,
  isNoShipOriginResponse,
  logShippingRateProvider,
  readShippingQuoteId,
  shippingOriginMissingMessage,
  storeRatesLive,
} from "@/services/shippingService";
import currency from "currency.js";
import helpers from "@/mixins/helpers";
import rules from "@/mixins/rules";
import store from "@/store";
import { loadStripe } from "@stripe/stripe-js";
import {
  buildTimePublishableKey,
  resolvePublishableKey,
} from "@/services/stripePublishableKey";
import StripeEvent from "@/models/StripeEvent";
import ValidateAddressDialog from "@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue";
import CartCheckout from "./CartCheckout.vue";
import CartItemList from "./CartItemList.vue";
import CartSummary from "./CartSummary.vue";
import { mapGetters } from "vuex";
import {
  storefrontCouponUrl,
  storefrontStoreUrl,
} from "@/components/Storefront/storefrontStoreApi";
import {
  departmentPickerMode,
  storeResolvesDestination,
} from "@/utils/campaign";
import { computeCartSubTotal } from "@/utils/cartSubTotal";
import {
  isPayrollPaymentMethod,
  isCardPaymentMethod,
} from "@/utils/storePaymentModel";
import { detectCampaignStaleness } from "./checkoutStalenessGuard.js";
// PR-R5: storefront URL restructure — `/cart`, `/orders`, `/order_confirmation`
// move to `/store/:slug/*`. Resolver pulls slug from route params or
// localStorage (set by /store/:id on first visit).
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { unref } from "vue";
import {
  STOREFRONT_STORE_ID,
  cartStoreArgs,
  normalizeStoreId,
} from "@/router/storefrontStoreId";
import { cartStoreMismatchMessage, fetchStoreCart } from "./storeCart";
import { quoteMemoFor, salesTaxQuoteKey } from "./checkoutQuoteMemo";
import {
  isUsableShippingQuote,
  liveShippingQuoteKey,
  liveShippingRequest,
  requestCheckoutQuote,
  shippingQuoteRefusalMessage,
  shipToQuoteLocation,
} from "./checkoutQuoteClient";
import { resolvePrimaryColorImageRef } from "@/utils/storefrontImages";
import { flatShippingCost } from "@/utils/flatShippingCents";
// PR-N1: variant resolver for decoration labels (see Cart.vue
// header for rationale).
import { buildCartDecorations } from "@/utils/decorationLabels";
import {
  cartPricesErrorMessage,
  fetchCartLinePrices,
  lineUnitCents,
} from "./cartLinePrices";
import { useResolveBatch } from "@/composables/useResolveBatch";
// White-label "Powered by" mark (card https://trello.com/c/mq26K91V).
// Shared with StorefrontFooter -- see the footer block
// in the template for why the cart needs its own binding.
import { resolveWhiteLabelMark } from "@/utils/whiteLabelMark";
import WhiteLabelMark from "@/components/WhiteLabelMark.vue";
import { cartQuoteKey, isServerTotals } from "./cartTotalsDisplay";
import { zipStateMismatch } from "@/utils/zipStatePolicy";
import {
  SUPPRESS_ERROR_TOAST,
  netCheckoutRefusalCode,
} from "@/interceptors/authResponseInterceptor";
import {
  composeCartChargeCents,
  halfUpCents,
  mulHalfUpCents,
} from "@/utils/cartChargeCents";
import {
  CONFIRM_DELAYS_MS,
  attemptFingerprint,
  attemptKeyFor,
  clearAttemptKey,
  confirmCheckoutAttempt,
  isAmbiguousFailure,
} from "./checkoutAttempt";

// How long the cart's inputs must stay put before the server breakdown is
// asked for: a quantity stepper or a typed zip settles first.
const SERVER_TOTALS_DEBOUNCE_MS = 300;

// How long the shopper's address must stay put before the quotes are asked
// for. Long enough that entering a five-digit zip is ONE request rather than
// five, and that correcting one field and then the next (State, then ZIP) is
// one burst-free pair of requests rather than one per field (#514: a console
// burst of failed rate calls followed by more as the second field was fixed).
// Short enough that the figure appears while they are still on the field.
const ADDRESS_QUOTE_DEBOUNCE_MS = 800;
const SALES_TAX_DEBOUNCE_MS = ADDRESS_QUOTE_DEBOUNCE_MS;
// Same idea for the live shipping quote fired from the address entry.
const SHIPPING_QUOTE_DEBOUNCE_MS = ADDRESS_QUOTE_DEBOUNCE_MS;

// `liveShippingQuoteKey` (checkoutQuoteClient.js) is the store-memo key for
// one live shipping quote, shared by `calculateSmartShipping`, the
// address-entry trigger and the admin Add New Order cart, so none of them can
// disagree about whether a stored quote is current.

/**
 * Failures already reported to the shopper. One quote request can be shared
 * by two callers (checkoutQuoteMemo.js) -- the address watcher and a
 * Continue click that joined it -- and each would otherwise toast the same
 * failure. One toast per failure (#2882). Keyed by the error / response
 * object the shared request produced, so a NEW attempt's failure is new.
 */
const reportedQuoteFailures = new WeakSet();

/**
 * One `calculateShippingCost` attempt, called with the cart as `this`.
 * `isCurrent()` turns false once a newer attempt starts; after every await
 * a superseded attempt returns without touching the cart.
 */
async function runShippingAttempt(isCurrent) {
  // A newer attempt owns the in-flight flag; an older one still waiting
  // never clears it (and the newer one may not ask a carrier at all).
  this.calculatingShipping = false;
  // Set again by calculateSmartShipping when a live quote is applied.
  this.clearShippingQuote();
  // Reset per attempt -- a retry that succeeds must be able to clear
  // TBD again, otherwise one transient failure strands the cart.
  this.calculatingShippingFailed = false;
  let res = true;
  if (this.campaign.shipping_cost_enabled) {
    // A pick-up location is rated like any other order (Bug Intake #548,
    // Jake 2026-10-26: free pick-up is dropped for MVP), so there is no
    // pick-up branch here: it used to return a $0 line unrated.
    //
    // Shipping is ENABLED but the store carries no shipping config at
    // all. `shipping_cost_info` is nullable with no server default and
    // no application default, so NULL is the ordinary state of a store
    // whose shipping was never set up -- it is not a corrupt row.
    //
    // Every branch below dereferences it directly (`.forEach` for
    // flat_rate_range, `.percentage` for cart_percentage), so a null
    // threw a TypeError out of this method. Nothing wraps this call, so
    // the throw unwound to an outer catch that dispatched `raiseError`
    // with a TypeError -- which has no `.response.data`, so the shopper
    // got GENERIC_ERROR_MESSAGE and a clean network tab.
    //
    // TBD, not $0. Same rule as the smart_shipping guard below and for
    // the same #2882 reason: an unconfigured rate is UNPRICED, not free.
    // `calculatingShippingFailed` holds `isShippingCostTBD` true below
    // and makes the awaited caller refuse to submit the order, rather
    // than promising free shipping nobody priced.
    //
    // A LIVE-RATE store needs no config at all (Live-rate shipping
    // card, Jake 2026-10-21): NULL type + NULL info is the ordinary
    // production store, and it rates live from the server-resolved
    // origin. This guard is for the FLAT types only.
    if (!this.ratesLive && !this.campaign.shipping_cost_info) {
      this.calculatingShippingFailed = true;
      return false;
    }
    // A live-rate store with NO shipping origin: no rate can exist and
    // checkout refuses the order. Hold TBD (never $0) and ask nothing
    // of the rate endpoint; the template shows who to contact.
    if (this.shippingOriginMissing) {
      this.calculatingShippingFailed = true;
      return false;
    }
    // A ZIP that is not in the chosen State is not an address a carrier can
    // rate: refuse it here too, so no caller (Continue, the AVS answer, a
    // coupon) can ask for a rate the address watcher would not (#514).
    if (this.shopperZipStateRefused) {
      this.calculatingShippingFailed = true;
      return false;
    }
    // A FLAT store: the estimate by the server's own rule against the items
    // subtotal (Bug Intake #549; utils/flatShippingCents.js). Checkout
    // charges the server's figure, so this one agrees with it. It used to
    // pick against `cartCharge`, which includes the shipping being chosen
    // (and tax and fees), so it could show a different band than checkout
    // charged. A config that cannot price the cart is TBD, never $0 or NaN.
    if (FLAT_SHIPPING_TYPES.includes(this.campaign.shipping_cost_type)) {
      const flat = flatShippingCost(this.campaign, this.total);
      if (flat === null) {
        this.calculatingShippingFailed = true;
        return false;
      }
      this.shippingCost = flat;
    }
    if (this.canCalculateSmartShipping) {
      res = await this.calculateSmartShipping(isCurrent);
      if (!isCurrent()) {
        return res;
      }
    }
  }

  // THE #2882 FIX, and it is a one-line change because the TBD state
  // already existed and was already wired through CartSummary and
  // CartCheckout. This used to clear the flag unconditionally, so a
  // FAILED rate lookup fell back to rendering `shippingCost` -- which
  // is 0 -- and the cart promised the shopper free shipping that
  // nobody had priced.
  //
  // Jake, 2026-08-26: "TBD for now is the most honest."
  if (!this.calculatingShippingFailed) {
    this.isShippingCostTBD = false;
  }
  return res;
}

/**
 * The newest `calculateShippingCost` attempt per cart instance:
 * `{ id, outcome }`. Same guard as `salesTaxRequestId`, kept off the
 * component's reactive data because nothing renders it. A shopper
 * correcting a zip produces two quotes that can answer in either order; an
 * attempt superseded while it waited applies NOTHING and resolves to the
 * newest attempt's outcome instead, so an awaiting caller (Continue to
 * Checkout) proceeds on the figure the cart actually shows.
 */
const shippingAttempts = new WeakMap();

function reportQuoteFailureOnce(store, failure, message) {
  if (failure && typeof failure === "object") {
    if (reportedQuoteFailures.has(failure)) return;
    reportedQuoteFailures.add(failure);
  }
  store.dispatch("raiseError", message);
}


/** Empty the page's and Vuex's cart lines; they now belong to `storeId`. */
function clearCartLines(vm, storeId) {
  vm.cart = [];
  vm.$store.commit("setCart", []);
  vm.cartStoreId = storeId;
}

// ── Checkout attempt (Bug Intake #637) ───────────────────────────────
//
// A Place Order whose answer was lost had still placed the order, and the
// shopper's retry built a second one. These helpers carry ONE key per checkout
// attempt (``./checkoutAttempt.js``) and confirm a lost answer with the server
// before anything is shown. Module functions taking the component, not
// methods, so the specs that run ``checkout`` / ``createPendingOrder`` on a
// hand-built ``this`` keep working.

const NOT_PLACED_MESSAGE = "Your order was not placed. Please try again.";

/** Where the attempt key lives: sessionStorage, or memory when it cannot. */
const memoryAttemptStorage = new Map();
function attemptStorage() {
  try {
    const storage = globalThis.sessionStorage;
    if (storage) return storage;
  } catch {
    // Storage access refused (privacy settings): fall back to memory.
  }
  return {
    getItem: (k) => (memoryAttemptStorage.has(k) ? memoryAttemptStorage.get(k) : null),
    setItem: (k, v) => memoryAttemptStorage.set(k, String(v)),
    removeItem: (k) => memoryAttemptStorage.delete(k),
  };
}

function attemptScope(vm) {
  return {
    storage: attemptStorage(),
    accountId: vm.$store.state.account._jv.id,
    storeId: normalizeStoreId(unref(vm.gateStoreId)),
  };
}

/**
 * The key for the attempt ``args`` describe: the same one while the shopper
 * is ordering the same thing (a retry), a new one once it changes. Money is
 * left out: it is DERIVED (a re-quote may move a cent) and must not turn a
 * retry into a new attempt; what is ordered, where and how is what counts.
 */
function attemptKeyForArgs(vm, args) {
  const fingerprint = attemptFingerprint({
    store: args.store_id,
    lines: (vm.cart || []).map((line) => [
      line.line_id != null ? line.line_id : line.id,
      line.quantity,
    ]),
    fields: args.checkout_fields,
    coupon: args.coupon_code || null,
    tender: args.tender || null,
    department: args.client_department_id || null,
    ship_to: args.buyer_location || null,
  });
  const key = attemptKeyFor({ ...attemptScope(vm), fingerprint });
  vm.checkoutAttemptKey = key;
  return key;
}

/** The attempt is settled (placed, or refused): the next one is new. */
function settleCheckoutAttempt(vm) {
  clearAttemptKey(attemptScope(vm));
  vm.checkoutAttemptKey = null;
}

/** Ask the server about attempt ``key``, showing "Confirming your order...". */
async function confirmAttempt(vm, key) {
  vm.confirmingOrder = true;
  try {
    return await confirmCheckoutAttempt({
      post: (...a) => vm.$axios.post(...a),
      accountId: vm.$store.state.account._jv.id,
      // The integer every cart RPC names (``cartStoreArgs``): the server
      // takes a positive integer only, and a string would 400 every check.
      storeId: cartStoreArgs(vm.gateStoreId).store_id,
      key,
      delaysMs: vm.confirmDelaysMs,
    });
  } finally {
    vm.confirmingOrder = false;
  }
}

/** Nothing could be confirmed: say so, keep the key so a retry is safe. */
function markUnconfirmed(vm) {
  vm.orderUnconfirmed = true;
}

/**
 * ``create_pending_order`` got no answer. Placed -> its confirmation;
 * pending -> carry on paying for THAT order; none -> not placed; unknown ->
 * could not confirm. Same return shape as ``createPendingOrder``.
 */
async function resolveLostPendingOrder(vm, key) {
  const { outcome, result } = await confirmAttempt(vm, key);
  if (outcome === "placed") {
    return showAlreadyPlacedOrder(vm, result.order_id, result.order_number, {
      deferred: !!result.deferred_payment,
    });
  }
  if (outcome === "pending") {
    return { id: result.order_id, deferred_payment: !!result.deferred_payment };
  }
  if (outcome === "none") {
    settleCheckoutAttempt(vm);
    vm.$store.dispatch("raiseError", NOT_PLACED_MESSAGE);
    return null;
  }
  markUnconfirmed(vm);
  return null;
}

/**
 * The attempt already went through: show its confirmation. Returns
 * ``{ id, alreadyPlaced: true }`` so CartCheckout touches no card.
 */
function showAlreadyPlacedOrder(vm, orderId, orderNumber, { deferred }) {
  const summary = confirmationSummaryOf(vm);
  applyPlacedOrder(vm, { id: orderId, orderNumber });
  vm.navigateToConfirmation(orderId, { ...summary, deferred });
  return { id: orderId, alreadyPlaced: true };
}

/**
 * ``checkout`` got no answer. Returns ``{ id, orderNumber }`` once the order
 * is known to be placed, else null (having said why). A ``pending`` order is
 * finished by RE-SENDING the same request, which the server answers
 * idempotently: a finalize repeat returns the order, a single-shot repeat
 * returns the order its key placed.
 */
async function recoverLostCheckout(vm, args, key) {
  const { outcome, result } = await confirmAttempt(vm, key);
  if (outcome === "placed") {
    return { id: result.order_id, orderNumber: result.order_number || null };
  }
  if (outcome === "pending") {
    try {
      const again = await postCheckout(vm, args);
      return placedOrderOf(again.data.meta.result);
    } catch (err) {
      if (isAmbiguousFailure(err)) {
        markUnconfirmed(vm);
        return null;
      }
      settleCheckoutAttempt(vm);
      vm.serverQuoteHeld = false;
      vm.$store.dispatch("raiseError", err);
      return null;
    }
  }
  if (outcome === "none") {
    // Definitively not placed: the quote is unspent, so the cart may ask
    // again. The address and cart are left exactly as the shopper had them.
    settleCheckoutAttempt(vm);
    vm.serverQuoteHeld = false;
    vm.$store.dispatch("raiseError", NOT_PLACED_MESSAGE);
    return null;
  }
  markUnconfirmed(vm);
  return null;
}

// POST the checkout RPC. One place, so the first send and the re-send of a
// lost one cannot drift apart. The failure is shown by ``checkout`` after an
// ambiguous one is confirmed: a toast first would tell the shopper a placed
// order failed.
function postCheckout(vm, args) {
  return vm.$axios.post(
    `/accounts/${vm.$store.state.account._jv.id}/checkout`,
    { meta: { method: "checkout", args } },
    { [SUPPRESS_ERROR_TOAST]: true }
  );
}

/** ``{ id, orderNumber }`` from a checkout answer. */
function placedOrderOf(result) {
  // The STORE-FORMATTED order number ("AC-2026-01001"), stashed for the
  // confirmation redirect. The DB id is what the route path and
  // ``fetchOrder`` are keyed on and stays the id; only the value the
  // shopper SEES changes. Previously the redirect pushed the id into the
  // ``orderNumber`` query param, so the live AdventHealth confirmation
  // read "Order Number #2" for order AC-2026-01001.
  //
  // Read from ``attributes`` FIRST, because this endpoint returns the ORM
  // Order (``account.py`` ``_finalize_order`` returns ``order``) and SAFRS
  // encodes a returned model as a JSON:API RESOURCE -- every column lands
  // under ``attributes``, and only ``id`` is top-level:
  //
  //     { type, id, attributes: { order_number, ... }, relationships }
  //
  // A bare ``result.order_number`` is therefore ``undefined`` on the wire,
  // which left this null and sent the shopper back to seeing the DB id.
  // The adjacent ``this.orderID`` read works for the very reason this one
  // did not: ``id`` IS a top-level JSON:API member.
  //
  // The flat fallback is kept deliberately: the sibling
  // ``create_pending_order`` returns a hand-built dict whose
  // ``order_number`` genuinely IS top-level, and admin/test callers post
  // here directly. Accepting both shapes costs nothing and means neither
  // caller can regress the other.
  return {
    id: result && result.id ? result.id : null,
    orderNumber:
      (result && result.attributes && result.attributes.order_number) ||
      (result && result.order_number) ||
      null,
  };
}

/**
 * The bookkeeping of a PLACED order, shared by the normal answer, a
 * recovered one and an already-placed replay. Returns the id.
 */
function applyPlacedOrder(vm, placed) {
  settleCheckoutAttempt(vm);
  // Placed: the shipping quote is spent. Drop it with the answer priced on
  // it; quoting stays held until `loadCart` has re-read the cart. The memo
  // goes too: it stores the whole quote answer, id included, so the next
  // cart to the same address and weight was priced on this SPENT id and
  // checkout refused it (Bug Intake #700).
  vm.shippingQuoteId = null;
  quoteMemoFor(vm, "shipping").clear();
  vm.serverQuote = null;
  vm.$store.state.checkout_fields = {};
  vm.$store.state.custom_checkout_fields = {};
  vm.$store.state.coupon = "";
  vm.$store.commit("setProductsLoaded", false);
  // The server emptied THIS store's cart; re-read it as the server has it
  // (loadCart puts it in Vuex too), never a local guess.
  vm.loadCart();
  vm.ccDialog = false;
  vm.orderID = placed.id ? placed.id : null;
  // The store-formatted number, stashed for the confirmation redirect.
  vm.orderNumber = placed.orderNumber || null;
  return placed.id ? placed.id : null;
}

/**
 * Checkout refused the posted shipping quote (Bug Intake #700): expired,
 * spent by an order already placed, or no longer matching the cart. Nothing
 * was built. Drop the quote -- the line, its id and the memo entry it came
 * from -- price shipping again, and tell the shopper what happened, so the
 * next Place Order posts a quote the server will take. Not the staleness
 * dialog: nothing about the campaign changed.
 */
async function handleShippingQuoteRefused(vm, message) {
  vm.clearShippingQuote();
  quoteMemoFor(vm, "shipping").clear();
  vm.$store.dispatch("raiseError", message);
  await vm.calculateShippingCost();
}

/**
 * The confirmation page's summary, captured BEFORE the order is applied:
 * ``applyPlacedOrder`` re-reads the (now empty) cart, and ``freeItemDiscount``
 * is derived from it -- read afterwards it would be 0 and silently drop the
 * row from the confirmation.
 */
function confirmationSummaryOf(vm) {
  return {
    itemCount: vm.cart ? vm.cart.reduce((sum, item) => sum + item.quantity, 0) : 0,
    total: vm.total != null ? vm.total.toFixed(2) : undefined,
    allowance: vm.companyAllowance > 0 ? vm.companyAllowance.toFixed(2) : undefined,
    charged: vm.cartCharge > 0 ? vm.cartCharge.toFixed(2) : undefined,
    freeItemDiscount: vm.freeItemDiscount > 0 ? vm.freeItemDiscount.toFixed(2) : undefined,
  };
}

export default {
  components: {
    "validate-address-dialog": ValidateAddressDialog,
    "cart-checkout": CartCheckout,
    "cart-item-list": CartItemList,
    "cart-summary": CartSummary,
    WhiteLabelMark,
  },

  mixins: [helpers, rules],

  inject: {
    // WHICH STORE this cart is for: the id StorefrontSessionGate resolved
    // from the URL slug. Never `localStorage.campaignId` -- see
    // router/storefrontStoreId.js. Null outside the gate, and then the cart
    // requests no store at all (it fails closed, below).
    gateStoreId: { from: STOREFRONT_STORE_ID, default: null },
  },

  watch: {
    // Ask the server for the breakdown whenever the cart's settled inputs
    // change (debounced); see `serverQuoteKey`.
    serverQuoteKey(key) {
      if (this.serverQuoteTimer) {
        clearTimeout(this.serverQuoteTimer);
        this.serverQuoteTimer = null;
      }
      if (!key) return;
      this.serverQuoteTimer = setTimeout(() => {
        this.serverQuoteTimer = null;
        this.fetchServerTotals();
      }, SERVER_TOTALS_DEBOUNCE_MS);
    },
    shipToShopperAddress(newVal, oldVal) {
      if (!newVal) {
        this.shopperLocationFormValid = false;
      }
      if (
        this.campaign.checkout_fields.includes("Location") &&
        this.campaign.locations.length == 1 &&
        !newVal
      ) {
        this.checkoutFieldsResult.selectedLocation = this.campaign.locations[0];
      } else {
        this.checkoutFieldsResult.selectedLocation = null;
      }
    },

    placeOrderButtonDisabled: async function() {
      if (
        !this.placeOrderButtonDisabled &&
        this.collectSalesTax &&
        this.campaign &&
        (!this.shipToShopperAddress ||
          (this.shipToShopperAddress && !this.shopperLocationFormValid))
      ) {
        await this.getSalesTax();
      }
      if (
        !this.placeOrderButtonDisabled &&
        this.campaign &&
        this.ratesLive &&
        (!this.shipToShopperAddress ||
          (this.shipToShopperAddress && !this.shopperLocationFormValid))
      ) {
        this.calculateShippingCost();
      } else if (
        !this.placeOrderButtonDisabled &&
        this.campaign.shipping_cost_enabled &&
        this.campaign &&
        (!this.shipToShopperAddress ||
          (this.shipToShopperAddress && !this.shopperLocationFormValid))
      ) {
        this.calculateShippingCost();
      }
    },

    /**
     * Quote a company / department store's destination (and its tax) as
     * soon as there is a priced cart, and again when it changes. Debounced
     * through the same timer as the live address lookup, so a run of
     * quantity steps produces one request.
     */
    /**
     * The shown destination answers ONE department pick. When the pick
     * changes, drop it at once: the old department's address must never
     * sit next to the new selection while the re-quote is debounced or in
     * flight (gate round 1).
     */
    "checkoutFieldsResult.selectedClientDepartmentId"(picked) {
      if (
        this.storeShipTo &&
        (picked || null) !== this.storeShipToDepartmentId
      ) {
        this.storeShipTo = null;
        this.storeShipToState = "loading";
      }
    },

    storeDestinationQuoteKey(key) {
      if (key == null) {
        return;
      }
      if (this.collectSalesTax) {
        this.scheduleSalesTaxRecalculation();
      } else {
        this.quoteStoreDestination();
      }
    },

    checkoutFieldsResult(newVal, oldVal) {
      if (newVal.selectedLocation) {
        this.shipToShopperAddress = false;
        this.shopperLocationFormValid = false;
      }
    },

    /**
     * Quote live shipping as soon as the shopper's ship-to is complete, and
     * again only when something the quote depends on changes (Jake,
     * 2026-10-22: "We shouldn't block that calculation on AVS, but if AVS
     * changes the zip code we should recalculate").
     *
     * Before this, the live quote ran only from Continue to Checkout (and
     * again after the AVS dialog), and only once EVERY checkout field was
     * filled -- the line read "TBD" through the whole address step.
     *
     * The key is the same one `calculateSmartShipping` stores its answer
     * under, so a change that leaves it alone (AVS rewriting the street, or
     * appending a ZIP+4) neither re-quotes nor blanks the line. A changed key
     * the line was not priced for shows TBD at once: the old figure is stale
     * (`clearShippingQuote`).
     * Nothing submits on it: Continue to Checkout (`calculateOrderTotals`)
     * awaits a fresh `calculateShippingCost` before the order is created, and
     * while TBD shows, `buildCheckoutArgs` posts `shipping_rate_available:
     * false`, which the server refuses.
     */
    shopperShippingQuoteKey(key) {
      if (this.shippingQuoteDebounceTimer) {
        clearTimeout(this.shippingQuoteDebounceTimer);
        this.shippingQuoteDebounceTimer = null;
      }
      if (key == null) {
        // The ship-to stopped being quotable (an incomplete address, a cart
        // being re-priced): the rate it had priced answers an address that
        // is no longer the one on screen. Nothing was applied, nothing to
        // clear.
        this.supersedeShippingAttempt();
        if (this.appliedShippingQuoteKey != null) {
          this.clearShippingQuote();
        }
        return;
      }
      if (key === this.appliedShippingQuoteKey && !this.isShippingCostTBD) {
        return;
      }
      // The old rate answers other inputs. Drop ALL of it, not just the
      // "TBD" flag: the figure, its server quote id and the key it was
      // stored under would otherwise keep feeding the subtotal and the
      // checkout until the re-quote lands -- or forever, if it never does.
      this.supersedeShippingAttempt();
      this.clearShippingQuote();
      this.shippingQuoteDebounceTimer = setTimeout(() => {
        this.shippingQuoteDebounceTimer = null;
        if (
          this.shopperShippingQuoteKey === this.appliedShippingQuoteKey &&
          !this.isShippingCostTBD
        ) {
          // Something else (Continue, the AVS answer) priced it meanwhile.
          return;
        }
        this.calculateShippingCost();
      }, SHIPPING_QUOTE_DEBOUNCE_MS);
    },

    /**
     * Recalculate the tax as the shopper TYPES their shipping address.
     *
     * Before this watcher existed, `getSalesTax()` had three call sites and
     * all three fired at checkout time -- the `placeOrderButtonDisabled`
     * watcher, `calculateOrderTotals()` (Continue to Checkout), and
     * `changedCompanyLocation()`. Nothing watched the address fields, so a
     * shopper could type a complete address and the tax line sat on its
     * deferred wording until they pressed Continue.
     *
     * DEEP, because `shopperLocation` is mutated FIELD BY FIELD in place all
     * the way down the chain: StorefrontCart owns the object, CartCheckout
     * passes it through, and CartShippingForm binds `v-model` straight onto
     * `localValue.zip_code` / `localValue.region`. The object identity never
     * changes, so a shallow watch would never fire.
     *
     * Only fires when BOTH zip and state are present -- see
     * `scheduleSalesTaxRecalculation` for why those two and nothing else.
     */
    shopperLocation: {
      deep: true,
      handler() {
        if (!this.shipToShopperAddress) {
          return;
        }
        const zip = (this.shopperLocation.zip_code || "").trim();
        const region = (this.shopperLocation.region || "").trim();
        if (!zip || !region) {
          return;
        }
        if (this.shopperZipStateRefused) {
          this.refuseSalesTaxForUnmatchedZip();
          return;
        }
        this.scheduleSalesTaxRecalculation();
      },
    },
  },

  data() {
    return {
      campaign: null,

      headers: [],

      checkoutFieldsResult: this.$store.state.checkout_fields || {},
      checkoutCustomDropdowns: [],
      customCheckoutFieldsResult:
        this.$store.state.custom_checkout_fields || {},

      saved: true,
      ccDialog: false,
      paymentMethodType: "card",
      currency: "usd",

      stripe: null,
      // A SECOND Stripe.js instance, loaded WITHOUT `stripeAccount` so it
      // operates on the PLATFORM account. Both are needed because the two
      // checkout halves genuinely live on different accounts, and
      // `stripeAccount` is fixed per instance -- there is no per-call
      // override:
      //
      //   * createPaymentIntent passes stripe_account=<connected>, so the
      //     immediate charge must confirm on `stripe` (connected).
      //   * createSetupIntent passes NO stripe_account, so the batched
      //     card-vaulting must confirm on `setupStripe` (platform).
      //
      // Confirming a platform SetupIntent on the connected instance is what
      // 404'd every card checkout on the live AdventHealth store
      // ("No such setupintent"). The platform side is the correct one to keep
      // for setup: `batch_charge.py` DECISION #10 requires platform-owned
      // cards so they can be CLONED onto the connected account at release,
      // and the Customer is platform-scoped too.
      setupStripe: null,
      // The build-time FALLBACK only. `loadData` overwrites this with the
      // per-store key the API resolves from the distributor's
      // `stripe_platform_mode`, before either `loadStripe` call above runs.
      // Seeded here (rather than left null) so a failed lookup degrades to
      // today's behaviour instead of `loadStripe(null)`.
      stripePublishableKey: buildTimePublishableKey(),
      isPayroll: false,
      // Whether the store takes a card at checkout -- the exact negation of
      // what the card-gating reads below actually mean. Kept separate from
      // ``isPayroll`` (used only for payroll-specific copy, e.g.
      // CartCheckout's allowance notice) because a payments-off store
      // (empty payment_method) is neither: no card to ask for, and not
      // payroll either (checkout refuses it outright).
      isCard: false,

      stripePending: false,
      flatRate: 0,
      collectSalesTax: false,
      collectProcessingFee: false,
      companyAllowance: 0,
      salesTax: "flat-rate" /* SalesTax can be flat-rate or tax-rate */,
      card: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      isCheckoutProcess: false,
      fetchingProducts: false,
      // The store read failed: show an explicit message, never a spinner
      // that never ends. See loadCampaign.
      storeUnavailable: false,

      shopperLocationFormValid: false,
      shipToShopperAddress: false,
      shopperLocation: {
        city: "",
        country: "US",
        region: "",
        street_and_number: "",
        suite_unit_etc: "",
        zip_code: "",
      },
      validatedAddress: {},
      // True when this checkout's address verification could NOT run
      // (ShipEngine limit / timeout / error, or our public ShipEngine
      // budget spent). Checkout proceeds with the address as entered, and
      // the order is marked via ``address_unverified`` so ops can check it
      // before it ships (Jake 2026-10-01). Also set when ShipEngine could
      // not MATCH the address and the shopper kept it as entered: verified
      // it was not, and ops needs that order most of all.
      addressVerificationUnavailable: false,

      // The destination a company / department store ships to, as the SERVER
      // resolves it (`checkout_quote` -> `ship_to`): the address, which
      // owner it belongs to, and whether it is complete. Null until quoted,
      // and always null on a shopper-address store. `storeShipToState` is
      // "idle" | "loading" | "loaded" | "failed"; a failed quote is NOT a
      // missing address (only `complete: false` blocks the order).
      storeShipTo: null,
      storeShipToState: "idle",
      // Monotonic id of the newest destination quote; an older answer that
      // lands late is dropped (same guard as `salesTaxRequestId`).
      storeShipToRequestId: 0,
      // The department pick the shown `storeShipTo` was quoted for.
      storeShipToDepartmentId: null,

      cartSalesTax: 0,
      salesTaxLoading: false,
      isSalesTaxTBD: true,
      // What the tax line should SAY. Four states, deliberately distinct,
      // because three of them used to render the same bare "TBD":
      //
      //   "not-calculated" -- nothing has been looked up yet. The shopper
      //                       sees "Calculated at checkout".
      //   "calculating"    -- a lookup is in flight.
      //   "calculated"     -- tax IS collected; show the figure. This
      //                       INCLUDES a genuine "$0.00" from a zero-RATE
      //                       jurisdiction -- see below, it is not an edge
      //                       case.
      //   "not-collected"  -- calculated, and the answer is no tax. Shows
      //                       "N/A". Covers all three ways a store collects
      //                       nothing: no nexus in the buyer's state, the
      //                       store not collecting tax at all, and a
      //                       tax_exempt company location.
      //
      // WHY "not-collected" IS ONE STATE AND NOT THREE (Jake + Mike,
      // 2026-09-14): "The store owner has asserted that they don't collect
      // tax in the state based on either their nexus settings, or on the
      // store being set to not collect tax." $0 is a CONFIGURED, intentional
      // state, not an anomaly to explain or block on. The cart reports it.
      //
      // WHY "$0.00" IS STILL SEPARATE FROM "N/A": a zero RATE inside a nexus
      // state is common, not theoretical. 1415 of 41091 priced zip rows in
      // api/src/tax_tables/ are rate 0.000000, and ALL 481 Oregon rows are
      // -- Oregon has no sales tax. Oregon sits in the reference
      // distributor's nexus_states, so "we collect tax here and the rate is
      // 0%" is that store's everyday case. It means something different from
      // "we collect no tax here", so it must read differently.
      //
      // `isSalesTaxTBD` is kept in LOCKSTEP -- true exactly when the state is
      // "not-calculated", false in the other three. It is deliberately NOT
      // replaced by a computed off `salesTaxState`, because `subTotal` reads
      // it to decide whether to fold tax into the running total, and
      // `getSalesTax` has always cleared it BEFORE the lookup resolves. A
      // computed would newly exclude tax from `subTotal` for the duration of
      // every "calculating" window -- a change to what the shopper sees as
      // their total, which is outside this fix. Keep the two in step by hand;
      // cart-live-sales-tax.spec.js asserts the invariant on every path.
      salesTaxState: "not-calculated",
      // Monotonic id of the most recently ISSUED tax lookup. A response is
      // applied only if its id still matches -- a shopper editing a zip can
      // easily produce two in-flight lookups, and the slower FIRST one must
      // not overwrite the fresher second one's answer.
      salesTaxRequestId: 0,
      // Pending debounce timer for the live address-driven lookup.
      salesTaxDebounceTimer: null,
      taxRate: 0,
      placingOrder: false,
      orderID: null,
      // Store-formatted order number from the checkout response
      // ("AC-2026-01001"). Distinct from orderID, which is the DB id the
      // route path and the order fetch are keyed on. Null until checkout
      // returns, and null for an order the backend left unnumbered.
      orderNumber: null,
      consolidateTaxFee: false,
      taxInfo: null,

      fundraisingAmountPerItem: 0,
      fundraisingType: null,
      coupon: this.$store.state.coupon || "",
      couponAmount: 0,
      couponType: "%",
      // A coupon lookup is in flight (blocks Apply and Place Order), and the
      // sequence number that makes a stale answer detectable. See applyCoupon.
      couponChecking: false,
      couponLookupSeq: 0,
      showNotificationDialog: false,
      notificationHeader: "",
      notificationBody: "",
      notificationBtn: "",
      shippingCost: 0,
      isShippingCostTBD: true,
      calculatingShipping: false,
      // True when the last rate attempt failed to produce a number, which
      // is what keeps `isShippingCostTBD` set (#2882). Distinct from
      // `calculatingShipping` (in flight) and from `shippingCost === 0`
      // (which is a legitimate carrier quote).
      calculatingShippingFailed: false,
      // A live quote answered "this store has no shipping origin"
      // (`unavailable; no-ship-origin`). Same meaning as the store read's
      // `shipping_origin_missing`, learned later (Live-rate shipping card).
      shipOriginMissingFromQuote: false,
      // The store-memo key of the live quote `shippingCost` currently shows,
      // or null when it shows none (TBD, a flat rate). Compared
      // with `shopperShippingQuoteKey` to tell a current line from a stale
      // one; the quote itself is stored in `checkoutQuoteMemo`.
      appliedShippingQuoteKey: null,
      // The SERVER's id for the live rate `shippingCost` shows (the
      // `X-Shipping-Quote-Id` header), or null. Checkout names it and is
      // charged the amount the server stored for it (Jake, 2026-10-22).
      shippingQuoteId: null,
      // Pending debounce timer for the address-entry shipping quote.
      shippingQuoteDebounceTimer: null,
      cartProcessingFee: currency(0),
      isCartProcessingFeeTBD: false,
      showMinChargeText: false,
      countriesAndCodes: [],
      costomDropdownsValid: true,
      appFee: currency(0),
      appFeeMultiplier: currency(0),
      // ThreadFlow platform fee context for THIS store, loaded in
      // loadData() from the slug-scoped endpoint. Folded into cartCharge
      // WITHOUT a visible line item.
      //
      // `null` until loaded -- NOT 0.0395. A hardcoded starting rate is a
      // guess about a per-store value, and on a zero-fee store it makes
      // the cart disagree with the server (a 409 at checkout). While it is
      // null the cart has no authoritative rate, so `platformFeeUnavailable`
      // blocks checkout rather than charging a number we invented.
      platformFeeRate: null,
      // Who bears the fee: "shopper" (default) or "distributor". Mirrors
      // the backend resolve_platform_fee_bearer.
      platformFeeBearer: "shopper",
      // Set when the fee read fails, so the shopper sees a real error
      // instead of a confidently wrong total.
      platformFeeUnavailable: false,

      // Server line prices (`cart_prices`): "idle" before the first load,
      // then "loading" | "loaded" | "error". Checkout is disabled unless
      // "loaded" -- the cart has no price of its own to fall back on.
      linePricesState: "idle",
      // The server's reason when it could not price the cart (a 409
      // PriceUnavailable names the product and size), or null.
      linePricesError: null,
      // Id of the newest loadCart; an older one that finishes later must
      // not write (#3884 gate P2-4).
      loadCartSeq: 0,
      // The store whose lines `cart` and `state.cart` hold, or null. A read
      // for a different store clears them first, so a failed read never
      // leaves another store's lines on this page.
      cartStoreId: null,
      // True once a load has priced every line, until a load fails. While
      // a later load re-prices, the last totals stay on screen, dimmed,
      // instead of the "Loading prices..." placeholder (#3884 gate P2-5).
      pricesLoadedOnce: false,
      // THE breakdown (Bug Intake #481): the newest `checkout_quote` answer,
      // `{ key, totals, cardCharge }`, where `key` names the inputs it was
      // asked for (`serverQuoteKey`). Shown only while that key is still the
      // cart's; see `serverTotals`.
      serverQuote: null,
      // Monotonic id of the newest breakdown request; an older answer that
      // lands late is dropped.
      serverQuoteRequestId: 0,
      serverQuoteTimer: null,
      // True from the moment checkout posts until the outcome is known (and,
      // once placed, until the cart is re-read): see `checkout`.
      serverQuoteHeld: false,
      panel1: [true],
      panel2: [false],
      isEmailConfirmed: false,
      // The address this session verified by code (CartCheckout's
      // `email-verified`), or null. On an email-domain store the allowance
      // belongs to it (card #596 follow-up), so it keys the server quote.
      verifiedEmail: null,

      cart: [],

      // Checkout staleness guard (PR #920 gate review P1 #1).
      // campaignSnapshot is a deep copy of `campaign` taken at load
      // time so we can detect admin edits made while the shopper is
      // mid-checkout. See checkoutStalenessGuard.js.
      campaignSnapshot: null,
      showStalenessDialog: false,
      stalenessDialogTitle: "",
      stalenessDialogBody: "",
      // stripeRemountKey is bumped when payment_method changes; the
      // child watches this and destroys its Stripe Elements so the
      // existing localCcDialog watcher re-mounts them next time.
      stripeRemountKey: 0,

      // Orphan-charge dialog (gate review P1 #3 — interim).
      // Surfaced when Stripe paymentIntent succeeded but the
      // subsequent checkout POST failed, leaving the card charged
      // with no Order. Names the paymentIntent.id so support can
      // reconcile manually. The structural fix (server-side
      // paymentIntent confirmation, or inverted ordering) is a
      // follow-up issue.
      showOrphanChargeDialog: false,
      orphanChargePaymentIntentId: "",

      // Bug Intake #637. The key of the checkout attempt in progress (sent as
      // ``checkout_attempt_key``; see ./checkoutAttempt.js), while a lost
      // response is being confirmed, and when it could not be.
      checkoutAttemptKey: null,
      confirmingOrder: false,
      orderUnconfirmed: false,
      confirmDelaysMs: CONFIRM_DELAYS_MS,

      domainIncorrectMessage:
        "Email address is not associated with an approved domain for this store. Please use a valid email address with an approved domain for this store",
    };
  },

  async mounted() {
    this.$nextTick(async () => {
      await this.loadCampaign();
      if (this.$store.state.account) {
        this.loadData();
        this.loadCart();
      }
    });
  },

  beforeUnmount() {
    // Drop any pending tax debounce so a timer cannot fire into a destroyed
    // component after the shopper navigates away mid-typing.
    if (this.salesTaxDebounceTimer) {
      clearTimeout(this.salesTaxDebounceTimer);
      this.salesTaxDebounceTimer = null;
    }
    if (this.shippingQuoteDebounceTimer) {
      clearTimeout(this.shippingQuoteDebounceTimer);
      this.shippingQuoteDebounceTimer = null;
    }
    if (this.serverQuoteTimer) {
      clearTimeout(this.serverQuoteTimer);
      this.serverQuoteTimer = null;
    }
  },

  async beforeRouteLeave(to, from, next) {
    // hit the backend to save the current cart
    await this.updateCart();

    // save any checkout fields in global state
    this.$store.state.checkout_fields = this.checkoutFieldsResult;
    this.$store.state.custom_checkout_fields = this.customCheckoutFieldsResult;
    this.$store.state.coupon = this.coupon;
    next();
  },

  computed: {
    ...mapGetters(["getPredefinedCheckoutFields"]),
    // The white-label decision for this store's cart footer. `campaign`
    // is null until the fetch lands, and the resolver renders today's
    // ThreadFlow mark for a null/absent answer -- so the footer neither
    // blanks nor throws in that window.
    whiteLabelMark() {
      return resolveWhiteLabelMark(this.campaign && this.campaign.white_label);
    },

    couponEnteredButNotApplied() {
      return this.coupon && !this.couponAmount;
    },

    // Client Portal v2 (PR C): typed client-department mapping (id + name)
    // surfaced by the storefront store read. Drives the 0 / 1 / N checkout picker rule
    // (0 -> no prompt, 1 -> auto-attribute server-side, N -> shopper must
    // pick). Always an array, even before the campaign loads.
    clientDepartments() {
      return (this.campaign && this.campaign.client_departments) || [];
    },
    // 'none' | 'auto' | 'required' -- the place-order gate requires a pick
    // only in the 'required' (N >= 2) case.
    departmentPickerMode() {
      return departmentPickerMode(this.clientDepartments);
    },

    /**
     * The department `checkout_quote` is asked with, or `undefined` while
     * the server would refuse to quote (UI gate, #481). On a store mapped
     * to 2+ departments the shopper must pick one, and the server answers
     * 400 "This store requires a department selection at checkout." with no
     * pick, or with an id that is not one of THIS store's departments (a
     * pick left in `checkout_fields` by another store). So the quote waits
     * for a pick from this store's list -- the same rule that shows the
     * picker (`departmentPickerMode`). 0 / 1 mapped need no pick.
     */
    quotableDepartmentId() {
      const picked = this.checkoutFieldsResult.selectedClientDepartmentId;
      if (this.departmentPickerMode !== "required") return picked || null;
      const known = this.clientDepartments.some(
        (dept) => picked != null && String(dept.id) === String(picked)
      );
      return known ? picked : undefined;
    },

    /**
     * A company / department store: the server, not the shopper, decides
     * where the order ships (``utils/campaign.storeResolvesDestination``).
     * The cart then quotes that destination and its tax via
     * ``checkout_quote`` instead of waiting for an address nobody enters.
     */
    storeResolvesDestination() {
      return storeResolvesDestination(this.campaign);
    },

    /**
     * A company store whose shopper must pick a department (2+ mapped,
     * `departmentPickerMode` "required") and has not yet. The server cannot
     * resolve -- and refuses to quote -- without the pick, so the cart waits
     * for it instead of asking and reporting a failure. The pick itself is
     * already required to place the order (`placeOrderButtonDisabled`).
     */
    storeDestinationAwaitsDepartment() {
      return Boolean(
        this.storeResolvesDestination &&
          this.departmentPickerMode === "required" &&
          // No pick, or one that is not this store's (#481): the server
          // refuses to quote either way.
          this.quotableDepartmentId === undefined
      );
    },

    /**
     * The server reported that nothing usable resolved: the order has
     * nowhere to ship, so Place Order is blocked and the destination block
     * says why. A quote that FAILED is not this -- that is "unknown", and
     * Continue to Checkout re-quotes.
     */
    storeDestinationMissing() {
      return Boolean(
        this.storeResolvesDestination &&
          this.storeShipTo &&
          this.storeShipTo.complete === false
      );
    },

    /**
     * Everything the store-resolved destination quote depends on. When it
     * changes (the cart priced or repriced, a department picked) the quote
     * is refreshed -- the destination is known up front, so its tax shows
     * without waiting for the checkout form. Null when there is nothing to
     * quote yet.
     */
    storeDestinationQuoteKey() {
      if (
        !this.campaign ||
        !this.storeResolvesDestination ||
        this.linePricesState !== "loaded" ||
        !this.cart ||
        this.cart.length === 0
      ) {
        return null;
      }
      return [
        this.total,
        this.checkoutFieldsResult.selectedClientDepartmentId || "",
      ].join("|");
    },

    /**
     * A live rate can be asked for: the store rates live and, when the
     * shopper ships to their own address, that address is complete.
     *
     * Deliberately NOT gated on `placeOrderButtonDisabled` any more (Jake,
     * 2026-10-22). That gate held the quote until name, email, department
     * and every custom field were filled -- none of which a shipping rate
     * depends on -- so the line read "TBD" until Continue to Checkout.
     */
    canCalculateSmartShipping() {
      return (
        this.ratesLive &&
        (!this.shipToShopperAddress ||
          (this.shipToShopperAddress && this.shopperLocationFormValid))
      );
    },

    /**
     * The store-memo key the shopper's ship-to would be quoted under right
     * now, or null when there is nothing to quote yet (not a live-rate store,
     * address incomplete, cart not priced, no shipping origin). Watched: see
     * the `shopperShippingQuoteKey` watcher.
     */
    /**
     * The shopper's own ship-to could not be rated: a live-rate store, the
     * shopper's address, and the last quote for it failed (no provider could
     * rate it, an empty answer, a 5xx, a dropped request). A store with no
     * shipping origin is the STORE's gap, not the address's, and says so
     * itself.
     *
     * An address nothing could rate has no trustworthy tax either (the zip
     * may not even belong to the state), so the tax line goes pending with
     * the shipping line (#515). Derived rather than assigned, so a tax
     * answer that lands after the failed quote cannot bring it back.
     */
    shippingAddressUnrated() {
      return Boolean(
        this.ratesLive &&
          this.shipToShopperAddress &&
          !this.shippingOriginMissing &&
          this.calculatingShippingFailed
      );
    },

    /** The tax state the tax line shows (see `shippingAddressUnrated`). */
    salesTaxStateShown() {
      return this.shippingAddressUnrated
        ? "not-calculated"
        : this.salesTaxState;
    },

    /** `isSalesTaxTBD` as the subtotal and the tax line read it. */
    salesTaxTBDShown() {
      return this.shippingAddressUnrated || this.isSalesTaxTBD;
    },

    /**
     * The shopper's ZIP clearly is not in the State they chose (NY with a
     * California ZIP): nothing is quoted for it -- no rate, no tax -- and the
     * address form shows a field error (#514, Jake 2026-10-23). Judged by
     * `@/utils/zipStatePolicy`, which fails open on anything it cannot place;
     * the server applies the same table and stays the authority.
     */
    shopperZipStateRefused() {
      if (!this.shipToShopperAddress) {
        return false;
      }
      const location = this.shopperLocation || {};
      return zipStateMismatch(location.zip_code, location.region, {
        country: location.country,
        stateNames: this.$store.getters.getStates,
      });
    },

    shopperShippingQuoteKey() {
      if (
        !this.campaign ||
        !this.campaign.shipping_cost_enabled ||
        !this.ratesLive ||
        !this.shipToShopperAddress ||
        !this.shopperLocationFormValid ||
        this.shippingOriginMissing ||
        // A ZIP outside the chosen State is not quoted (#514): a null key
        // makes the watcher supersede any attempt in flight and clear the
        // rate, so nothing stale survives.
        this.shopperZipStateRefused ||
        this.linePricesState !== "loaded" ||
        !this.cart ||
        this.cart.length === 0
      ) {
        return null;
      }
      return liveShippingQuoteKey(
        this.campaign,
        { ...this.shopperLocation, isCompanyLocation: false },
        this.cartTotalWeight
      );
    },

    cartTotalWeight() {
      let weight = 0;
      this.cart.forEach((product) => {
        if (product && product.campaignProduct.product_weight) {
          let productWeight = product.campaignProduct.product_weight;
          // convert weight into weight in grams
          if (product.campaignProduct.product_unit == "Ounce") {
            productWeight *= 28.35;
          }
          if (product.campaignProduct.product_unit == "Pound") {
            productWeight *= 454;
          }
          weight += productWeight * product.quantity;
        } else {
          // if product has no weight associated with it (for some reason) add 454 grams per item
          weight += 454 * product.quantity;
        }
      });
      return weight;
    },

    computedSalesTax() {
      if (this.cartSalesTax);

      // An address nothing could rate carries no tax figure: a failed tax
      // lookup already zeroes it, and so does this (#515).
      if (this.shippingAddressUnrated) {
        return 0;
      }

      // if user currently placing order with shopper location, return tax calculated by getSalesTax()
      if (this.shipToShopperAddress) {
        return this.cartSalesTax;
      }
      // if user currently building order with company location, return calculated sales tax
      // calculated sales tax gets the rate from an initial tax calculation, and applies that rate to
      // the order as the user updates the order
      else {
        return this.taxRate * this.total;
      }
    },

    total() {
      let total = 0;
      if (this.cart)
        {this.cart.forEach((orderItem) => {
          total +=
            this.applyFundraisingAmount(orderItem.price) * orderItem.quantity;
        });}

      return total;
    },

    cartTotal() {
      const amount = this.total - this.companyAllowance;

      return amount;
    },

    // Highest-priced-item-free (AH, https://trello.com/c/T756dpsG).
    //
    // MIRRORS api/src/pricing/free_item.py EXACTLY. This value is folded
    // into cartCharge below, and the server recomputes the same number
    // from its OWN prices in _validate_checkout_totals -- a disagreement
    // is a hard 409 that reloading cannot fix, so the two implementations
    // must stay in lockstep:
    //
    //   * highest UNIT price wins, not the biggest line total
    //   * exactly ONE unit is free, never the whole line
    //   * ties break on the line's cart INDEX, zero-padded, so both sides
    //     pick the same line regardless of iteration order
    //   * the unit price compared is the FUNDRAISING-ADJUSTED price, which
    //     is what `total` above sums and therefore what the server's
    //     snapshot pipeline produces
    //   * integer cents, so neither side re-rounds a float
    //
    // Returns 0 when the store has the rule switched off, which is every
    // store except AdventHealth's.
    freeItemDiscount() {
      if (!this.campaign || !this.campaign.free_highest_priced_item_enabled) {
        return 0;
      }
      if (!this.cart || !this.cart.length) {
        return 0;
      }

      let winner = null;
      this.cart.forEach((orderItem, index) => {
        const quantity = Number(orderItem.quantity) || 0;
        if (quantity <= 0) {
          return;
        }
        const unitCents = currency(
          this.applyFundraisingAmount(orderItem.price)
        ).intValue;
        if (unitCents <= 0) {
          return;
        }
        const key = String(index).padStart(4, "0");
        if (
          winner === null ||
          unitCents > winner.unitCents ||
          (unitCents === winner.unitCents && key < winner.key)
        ) {
          winner = { unitCents, key };
        }
      });

      return winner === null ? 0 : winner.unitCents / 100;
    },

    // FIXME: This computed has side effects — it mutates appFee, cartProcessingFee,
    // isCartProcessingFeeTBD, and showMinChargeText. These should be refactored into
    // a watcher or explicit method calls. Preserved as-is to avoid behavioral changes.
    cartCharge() {
      // THE CHARGE, IN WHOLE CENTS, BY THE SERVER'S ONE RULE (#481, gate
      // round 3): every component rounded half-up on its decimal form, in
      // compute_order_money's order -- tax, coupon, allowance, shipping, the
      // free item (after them and BEFORE the card fees, so moving it changes
      // the processing fee), the Stripe fee, the platform fee, the $0.50
      // minimum. See utils/cartChargeCents.js. MUST STAY IN LOCKSTEP WITH
      // order_money.py: checkout compares this against the server recompute
      // to the cent. Both are pinned by one table
      // (api/tests/fixtures/cart_charge_parity.json;
      // __tests__/cart-charge-parity.spec.js).
      //
      // THE PLATFORM FEE: the shopper pays it on card AND payroll stores by
      // default (Jake, 2026-10-22); `shopperBearsPlatformFee` is THE one
      // condition, mirroring the server's `shopper_pays_platform_fee`. The
      // bearer decides the SHOPPER'S TOTAL only (Option A, 2026-09-11).
      const money = composeCartChargeCents({
        items: this.total,
        collectSalesTax: this.campaign.collect_sales_tax,
        salesTax: this.computedSalesTax,
        couponType: this.couponType,
        couponAmount: this.couponAmount,
        allowance: this.companyAllowance > 0 ? this.companyAllowance : 0,
        shippingEnabled: this.campaign.shipping_cost_enabled,
        shipping: this.shippingCost,
        freeItemCents: halfUpCents(this.freeItemDiscount),
        isCard: this.isCard,
        collectProcessingFee: this.campaign.collect_processing_fee,
        platformFeeCents: this.shopperBearsPlatformFee
          ? halfUpCents(this.platformFeeAmount)
          : 0,
      });

      if (this.isCard) {
        // THE APP FEE IS COMPUTED BUT NOT CHARGED (see order_money.py's
        // matching block): the legacy 3.95% duplicating the platform fee.
        this.appFee = this.calculateApplicationFee(
          money.preFeeCents / 100,
          this.appFeeMultiplier
        );
        // The Stripe fee, for the fee line; in the charge only when the
        // store collects it.
        this.cartProcessingFee = currency(money.stripeFeeCents / 100);
      }

      this.isCartProcessingFeeTBD = false;
      this.showMinChargeText = money.minimumChargeCents > 0;

      return money.chargedCents / 100;
    },

    locationPopulated() {
      // DEAD CODE, and harmless -- do not "fix" it, and do not delete it as
      // part of an unrelated change.
      //
      // The stray semicolon makes this an empty statement, so the guard has
      // never done anything. Investigated 2026-09-08 while chasing the
      // disabled "Continue to Checkout" button: `git show dd5d8d033^` has the
      // function WITHOUT this line, so dd5d8d033 ("Fix Issue #1534", 2021)
      // ADDED it already broken. It was presumably meant as a null-guard on
      // `checkoutFieldsResult`.
      //
      // It cannot be the cause of anything, because the value can never be
      // null: `data()` initialises it as
      // `this.$store.state.checkout_fields || {}`. The identical pattern
      // exists in `computedSalesTax` (`if (this.cartSalesTax);`).
      if (this.checkoutFieldsResult);

      if (
        !this.campaign.checkout_fields.includes("Location") &&
        !this.campaign.checkout_fields.includes("Shopper Location")
      )
        {return true;}

      let populated = false;

      if (this.shipToShopperAddress) {
        populated = this.shopperLocationFormValid;
      } else {
        populated = this.checkoutFieldsResult.selectedLocation != null;
      }
      return populated;
    },

    /**
     * The inputs `checkout_quote` is asked with, or null while the cart has
     * not settled them.
     *
     * Bug Intake #481 (gate round 1 P1): the cart shows the SERVER's
     * breakdown, not its own arithmetic. The server can only answer for
     * figures that exist, so nothing is asked while the lines are pricing,
     * the tax or the shipping rate is being looked up (or has not been: no
     * address yet), or a coupon is being checked. Until then the cart shows
     * its own figures, marked "(estimated)".
     *
     * The tax location is the one checkout sends (`buildCheckoutArgs`); a
     * company / department store sends none -- the server resolves its
     * destination, as `quoteStoreDestination` does.
     */
    serverQuoteArgs() {
      if (this.serverQuoteHeld) return null;
      const campaign = this.campaign;
      if (!campaign || !campaign.storefront_pricing) return null;
      const account = this.$store.state.account;
      if (!account || !account._jv) return null;
      if (this.linePricesState !== "loaded" || this.repricing) return null;
      if (!this.cart || this.cart.length === 0) return null;
      if (this.salesTaxLoading || this.calculatingShipping) return null;
      if (this.couponChecking) return null;
      if (
        this.collectSalesTax &&
        !["calculated", "not-collected"].includes(this.salesTaxStateShown)
      ) {
        return null;
      }
      if (campaign.shipping_cost_enabled && this.isShippingCostTBD) return null;
      const departmentId = this.quotableDepartmentId;
      if (departmentId === undefined) return null;
      const storeId = this.campaign._jv ? this.campaign._jv.id : null;
      let location = null;
      if (!this.storeResolvesDestination) {
        const found = this.shipToShopperAddress
          ? this.shopperLocation
          : this.checkoutFieldsResult.selectedLocation;
        location = found
          ? {
              ...this.formatLocation(found),
              isCompanyLocation: !this.shipToShopperAddress,
            }
          : null;
      }
      return {
        accountId: account._jv.id,
        storeId,
        lines: this.cart,
        itemsTotal: this.total,
        shippingCost: this.shippingCost > 0 ? this.shippingCost : 0,
        // The live rate's server quote (#4725): checkout charges what the
        // server stored under it, so the breakdown is priced from it too.
        shippingQuoteId: this.shippingQuoteId || null,
        couponCode:
          this.couponAmount && this.couponAmount > 0 && this.coupon
            ? this.coupon.trim()
            : null,
        departmentId,
        location,
        holderEmail: this.verifiedEmail || null,
      };
    },

    /** The identity of `serverQuoteArgs`, or null when unsettled. */
    serverQuoteKey() {
      const args = this.serverQuoteArgs;
      return args ? cartQuoteKey(args) : null;
    },

    /**
     * THE breakdown the cart shows: the server's answer for the cart's
     * CURRENT inputs, or null (no answer yet, a stale one, a failed one).
     * CartSummary and the payment dialog render it; null makes them show
     * the cart's own figures marked "(estimated)".
     */
    serverTotals() {
      const quote = this.serverQuote;
      if (!quote || !this.serverQuoteKey || quote.key !== this.serverQuoteKey) {
        return null;
      }
      return quote.totals;
    },

    // A re-price after the cart was already priced: the last totals stay
    // up, dimmed, until the new prices land (#3884 gate P2-5).
    repricing() {
      return (
        this.pricesLoadedOnce &&
        (this.linePricesState === "loading" || this.linePricesState === "idle")
      );
    },

    /**
     * What the Place Order button actually honors: the form gate, PLUS a
     * coupon lookup in flight (an order must not be placed against a
     * discount still being decided). Kept separate from
     * `placeOrderButtonDisabled` because that one is WATCHED to trigger the
     * tax and shipping recalculation, which a coupon check must not re-fire.
     */
    placeOrderBlocked() {
      return Boolean(
        this.placeOrderButtonDisabled ||
          this.couponChecking ||
          this.shippingOriginMissing
      );
    },

    /**
     * True when this store charges shipping from a LIVE rate (Jake,
     * 2026-10-21): shipping enabled and no flat setting -- `smart_shipping`
     * or no setting at all. Mirrors the server's `store_rates_live`.
     */
    ratesLive() {
      return storeRatesLive(this.campaign);
    },

    /**
     * The store rates live but has NO shipping origin, so no rate can
     * exist and checkout refuses the order. Known from the store read, or
     * from a quote that answered `no-ship-origin`.
     */
    shippingOriginMissing() {
      return Boolean(
        this.ratesLive &&
          (this.campaign.shipping_origin_missing === true ||
            this.shipOriginMissingFromQuote)
      );
    },

    /** The persistent error naming who to contact (reply-to, else Owner). */
    shippingOriginErrorMessage() {
      return shippingOriginMissingMessage(
        this.campaign && this.campaign.shipping_support_email
      );
    },

    placeOrderButtonDisabled() {
      // only do all of this if the campaign is loaded to avoid errors
      let isDisabled = false;
      if (this.campaign) {
        const fields = this.campaign.checkout_fields;
        if (
          (fields &&
            fields.includes("First Name") &&
            !this.checkoutFieldsResult.firstName) ||
          (fields.includes("Last Name") &&
            !this.checkoutFieldsResult.lastName) ||
          (fields.includes("Company Email") &&
            (!this.checkoutFieldsResult.companyEmail ||
              !(
                this.validateEmail(this.checkoutFieldsResult.companyEmail) ==
                true
              ))) ||
          (fields.includes("Manager") &&
            this.campaign.managers.length > 0 &&
            !this.checkoutFieldsResult.selectedManager) ||
          // Client Portal v2 (PR C): require a department pick ONLY for
          // N-department stores (2+ mapped). 0 -> no prompt; 1 -> auto.
          (this.departmentPickerMode === "required" &&
            !this.checkoutFieldsResult.selectedClientDepartmentId) ||
          ((fields.includes("Location") ||
            fields.includes("Shopper Location")) &&
            !this.locationPopulated) ||
          // Guarded on the dropdown COUNT, not on the flag alone. The flag
          // is a Vuetify `<v-form>` aggregate, which is three-state and
          // stays `null` until every field inside has been validated -- and
          // a form wrapping zero fields never validates anything, so it
          // stays `null` forever. A store with no custom dropdowns (the live
          // AdventHealth store is one) would otherwise be gated permanently
          // by a form that has nothing to say. Same guard the store editor
          // already applies to its identically-shaped flag
          // (StoreCheckoutInfo.vue:605, :817).
          (!this.costomDropdownsValid &&
            this.checkoutCustomDropdowns.length !== 0)
        ) {
          isDisabled = true;
        }

        if (this.cart.length === 0) {
          isDisabled = true;
        }
        // A company / department store with nowhere to ship: the order
        // cannot be placed (the destination block says why).
        if (this.storeDestinationMissing) {
          isDisabled = true;
        }
        // No server prices, no checkout (2026-09-29): the totals would be
        // built from lines the server has not priced.
        if (this.linePricesState !== "loaded") {
          isDisabled = true;
        }
        this.customCheckoutFields.forEach((field) => {
          if (!this.customCheckoutFieldsResult[field]) {
            isDisabled = true;
          }
        });
        return isDisabled;
      } else {
        return true;
      }
    },

    customCheckoutFields() {
      return this.campaign.checkout_fields.filter(
        (obj) =>
          this.getPredefinedCheckoutFields &&
          this.getPredefinedCheckoutFields.indexOf(obj) === -1
      );
    },

    // Whether to RENDER the payment section. Must agree with
    // ``openPaymentOrPlaceOrder``, the gate that decides whether to ASK for
    // a card -- a store that shows a card form must ask for a card, and one
    // that asks must have shown the form. Both now read the same two
    // conditions; see that method for why ``storefront_pricing`` is not one
    // of them.
    showCCInfo() {
      return this.isCard && this.cartCharge > 0;
    },

    // The DISPLAYED subtotal. Composition lives in utils/cartSubTotal.js so
    // it can be driven directly by tests -- this computed cannot be mounted
    // in isolation (Vuex + Stripe + router), and a test harness that
    // re-implements the arithmetic proves only that the copy agrees with
    // itself.
    //
    // NOT the charged amount: `cartCharge` above owns that, and the server
    // recomputes it independently at checkout. The processing fee is passed
    // in as the SAME figure CartSummary renders on the fee line
    // (`cartProcessingFee.add(appFee).value`) rather than recomputed from a
    // rate here -- a second definition of what a fee costs is how a
    // displayed total and a charged total drift apart.
    //
    // ORDER OF EVALUATION IS LOAD-BEARING, AND THIS IS THE SUBTLE PART.
    // `cartProcessingFee` and `appFee` are not independent state -- they are
    // ASSIGNED as a side effect of the `cartCharge` computed (see the FIXME
    // there). Reading them without also reading `cartCharge` would give this
    // computed no dependency on the thing that produces them, so Vue would
    // be free to serve a cached subtotal built from the PREVIOUS cart's fee
    // and the figure would render one edit behind.
    //
    // `processingFeeForSubTotal` below fixes that by reading `cartCharge`
    // into a named computed of its own, so the dependency is structural
    // rather than a bare statement a refactor could drop as dead code.
    //
    // NOTE what is NOT happening: `cartCharge`'s value is never added here.
    // It is the CHARGED total, this is the DISPLAYED subtotal, and
    // conflating them is how the two drift apart.
    subTotal() {
      return computeCartSubTotal({
        total: this.total,
        salesTax: this.computedSalesTax,
        collectSalesTax: this.collectSalesTax,
        isSalesTaxTBD: this.salesTaxTBDShown,
        couponAmount: this.couponAmount,
        couponType: this.couponType,
        shippingCost: this.shippingCost,
        shippingEnabled: this.campaign.shipping_cost_enabled,
        freeItemDiscount: this.freeItemDiscount,
        processingFee: this.processingFeeForSubTotal,
        collectProcessingFee: this.collectProcessingFee,
        isProcessingFeeTBD: this.isCartProcessingFeeTBD,
        // The platform fee, on the same condition `cartCharge` applies it.
        // Without this the displayed subtotal sits BELOW the Total by the
        // fee, which is the defect: Subtotal 17.81, Total 18.48, nothing on
        // screen for the 67c (order AT-2026-01033).
        platformFee: this.platformFeeAmount,
        shopperBearsPlatformFee: this.shopperBearsPlatformFee,
      });
    },

    // The processing fee as a single figure, and the seam that keeps the
    // subtotal reactive.
    //
    // `cartCharge` is read FIRST and its result discarded. That read is the
    // whole point of this computed: `cartProcessingFee` and `appFee` are
    // assigned as a side effect of evaluating `cartCharge`, so anything that
    // wants a current fee has to depend on it. Deleting the first line makes
    // the subtotal render one cart-edit behind, which is a stale MONEY
    // figure and will not look like a caching bug when it happens.
    //
    // THE FIGURE IS `cartProcessingFee` ALONE. It used to be
    // `cartProcessingFee.add(appFee)`, which was correct only while `appFee`
    // was ADDED to the charge. It no longer is -- `appFee` is the legacy
    // 3.95% duplicating the canonical platform fee, and `cartCharge` above
    // stopped folding it in -- so keeping the sum would SHOW the shopper a
    // fee ~3.95% of the cart larger than what their card is charged, and the
    // displayed Subtotal would inherit the overstatement (computeCartSubTotal
    // folds in exactly this figure).
    //
    // Deliberately not recomputed from a rate: a second definition of what a
    // fee costs is how a displayed total and a charged total drift apart.
    // Pinned by __tests__/cart-fee-line-matches-the-charge.spec.js, which
    // differences `cartCharge` to derive the fee ACTUALLY in the charge and
    // requires this to equal it.
    processingFeeForSubTotal() {
      const charge = this.cartCharge;
      if (charge === null || charge === undefined) {
        return 0;
      }
      return currency(this.cartProcessingFee).value;
    },

    /**
     * Whether the SHOPPER bears the ThreadFlow platform fee.
     *
     * THE SAME CONDITION `cartCharge` USES, read from one place so the row
     * the shopper sees and the fee their card takes cannot disagree. Only
     * the exact string "distributor" moves the fee off the shopper; every
     * other value -- unset, null, unrecognised -- charges them, mirroring
     * the backend's `resolve_platform_fee_bearer` fail-safe.
     *
     * The rate check is part of the condition, not an afterthought: an
     * unloaded rate is null, `currency().multiply(null)` is 0, and a
     * "Service Fee: $0.00" row asserts a figure the cart does not yet have.
     * Checkout is separately blocked while the rate is missing
     * (`platformFeeUnavailable`).
     *
     * Card AND payroll stores (Bug Intake #481); a store that takes no
     * shopper payment at all charges no fee -- the server's
     * `shopper_pays_platform_fee`.
     */
    shopperBearsPlatformFee() {
      return (
        (this.isCard || this.isPayroll) &&
        this.platformFeeBearer !== "distributor" &&
        typeof this.platformFeeRate === "number" &&
        !isNaN(this.platformFeeRate)
      );
    },

    /**
     * The platform fee for this cart, in dollars -- the figure the Service
     * Fee line renders and the figure folded into the displayed subtotal.
     *
     * MUST match the addend in `cartCharge` exactly: same base (`total`, the
     * ITEMS subtotal, NOT the post-tax/coupon/allowance/shipping charge),
     * same rate, same currency() half-up cent rounding. That is what closes
     * the 67c gap Jake saw on order AT-2026-01033 rather than papering over
     * it with a second, nearly-equal number.
     *
     * Returns 0 when the shopper does not bear the fee, so both the row and
     * the subtotal fold drop out together.
     */
    platformFeeAmount() {
      if (!this.shopperBearsPlatformFee) {
        return 0;
      }
      // Half-up whole cents of the items subtotal x rate, exactly
      // (cartChargeCents.js), the server's compute_platform_fee rule.
      return mulHalfUpCents(halfUpCents(this.total), this.platformFeeRate) / 100;
    },
  },

  methods: {
    // ── Child event handlers ──────────────────────────────────────────

    handleEmailConfirmed(data) {
      this.companyAllowance = data.allowance;
      if (data.notification) {
        this.notificationHeader = data.notification.header;
        this.notificationBody = data.notification.body;
        this.notificationBtn = data.notification.btn;
        this.showNotificationDialog = true;
      }
    },

    /**
     * CartCheckout reports the address this session verified (or null when
     * the shopper edited it). On an email-domain store the allowance belongs
     * to the VERIFIED address (card #596 follow-up): until it is verified
     * the server shows no balance, so re-read it now -- the posted charge is
     * computed from it, and the server refuses a charge that left the
     * allowance out. The quote re-asks on its own (`serverQuoteKey` carries
     * the address).
     */
    handleEmailVerified(email) {
      this.verifiedEmail = email || null;
      if (email) {
        this.loadV2RemainingAllowance();
      }
    },

    handleLocationChanged() {
      this.changedCompanyLocation();
    },

    handleCartUpdated() {
      this.loadCart();
      if (this.canCalculateSmartShipping && !this.shipToShopperAddress) {
        this.calculateShippingCost();
      } else if (!this.ratesLive) {
        this.calculateShippingCost();
      }
    },

    async handlePaymentComplete(data) {
      // Capture order summary before checkout clears the cart (see
      // ``confirmationSummaryOf``: the free-item row depends on it).
      const summary = confirmationSummaryOf(this);
      const preCheckoutItemCount = summary.itemCount;
      const preCheckoutTotal = summary.total;
      const preCheckoutAllowance = summary.allowance;
      const preCheckoutCharged = summary.charged;
      const preCheckoutFreeItem = summary.freeItemDiscount;

      // ── Pre-create-before-charge finalizer (Approach A) ───────────
      //
      // The Order already EXISTS (status payment_pending): it was created by
      // createPendingOrder() BEFORE the card was charged, and the card was
      // charged against it (server-authoritative amount keyed by order_id).
      // The paymentIntent has succeeded by the time this runs (CartCheckout
      // confirms before emitting). Now FINALIZE that order: transition it to
      // processing + run the deferred side effects, by passing its id into
      // checkout().
      //
      // ``data.orderId`` is the pre-created order id (emitted by CartCheckout
      // alongside the paymentIntent). If it is missing (e.g. a legacy client
      // that charged without pre-creating), fall back to the no-order_id
      // build+finalize path so an orphan charge is still reconciled.
      //
      // On finalize failure the order remains payment_pending and the cleanup
      // sweep will reconcile it; surface the orphan-charge dialog naming the
      // paymentIntent.id for support and do NOT navigate.
      // The PaymentIntent id is REQUIRED by the finalizer
      // (https://trello.com/c/nJog08xa): checkout will not promote the order
      // or fire any paid-order side effect without it. The backend checks it
      // against the id IT recorded when it created the PaymentIntent, so this
      // is a cross-check of the server's own record, not a value the server
      // trusts.
      const order_id = await this.checkout(
        data.orderId || null,
        data.paymentIntent ? data.paymentIntent.id : null,
        data.setupIntent ? data.setupIntent.id : null
      );
      if (!order_id) {
        // The answer was lost and could not be confirmed either way (Bug
        // Intake #637): the order may well be placed, so this is NOT an
        // orphan charge. The "could not confirm" notice is already up.
        if (this.orderUnconfirmed) return;
        // Finalize failed.
        //
        // On the CHARGED path the card has been debited and the order is still
        // pre-payment (the sweep reconciles it), so the orphan-charge dialog
        // names the PaymentIntent for support.
        //
        // On the DEFERRED path NO money has moved -- only a card was vaulted --
        // so there is no orphan charge to recover and that dialog would tell
        // the shopper something false. The error the checkout call already
        // raised is the correct and complete signal.
        if (data.paymentIntent) {
          this.orphanChargePaymentIntentId = data.paymentIntent.id;
          this.showOrphanChargeDialog = true;
        }
        return;
      }

      if (!data.paymentIntent) {
        // DEFERRED (BATCHED) checkout: the card was saved, nothing was
        // charged. ``stripe_events`` records CHARGES, so writing a row here
        // would report revenue that does not exist -- the table feeds five
        // financial reports and the SuperAdmin revenue metric.
        // ``batch_charge`` writes the record when money actually moves.
        this.navigateToConfirmation(order_id, {
          itemCount: preCheckoutItemCount,
          total: preCheckoutTotal,
          allowance: preCheckoutAllowance,
          charged: preCheckoutCharged,
          freeItemDiscount: preCheckoutFreeItem,
          deferred: true,
        });
        return;
      }

      data.stripeEvent.order_id = order_id;
      data.stripeEvent.stripe_charge_id = data.paymentIntent.id;
      data.stripeEvent.status = "charge_successful";

      // Recording the charge event must NEVER block the shopper's
      // confirmation. This POST 403s for EVERY shopper: /api/stripe_events is
      // gated on `superadmin.settings` (models/stripe_event.py) and no shopper
      // role holds that capability -- verified live on 8088, which returns
      //   {"error": "Needs capability 'superadmin.settings',
      //             role 'Shopper' does not have it"}
      // Previously this was an unguarded `await`, so the rejection aborted
      // handlePaymentComplete before the redirect below: the card was charged,
      // the order existed, and the shopper was left sitting on the cart with no
      // confirmation. A failed RECORDING write is not a failed PAYMENT.
      //
      // The 403 itself is deliberate on the backend (that model's comment calls
      // the SuperAdmin-only gate defence-in-depth), so the fix belongs here, on
      // the caller, not in widening the capability.
      try {
        await this.$store.dispatch("jv/post", [
          data.stripeEvent,
          { url: "/stripe_events" },
        ]);
      } catch (e) {
        // Surfaced, not silenced -- the write genuinely failed and someone
        // should be able to see that in the console without the shopper paying
        // for it with a lost confirmation.
        console.warn("stripe_events write failed (non-blocking):", describeError(e));
      }
      if (data.paymentIntent.status === "succeeded") {
        this.navigateToConfirmation(order_id, {
          itemCount: preCheckoutItemCount,
          total: preCheckoutTotal,
          allowance: preCheckoutAllowance,
          charged: preCheckoutCharged,
          freeItemDiscount: preCheckoutFreeItem,
        });
      }
    },

    // Convergence (card q7EQ3a0b): confirmation URL is
    // /shop/:slug/orders/:id/confirmation. The legacy /order_confirmation and
    // /store/:slug/... paths are still mapped via REDIRECTS for in-flight
    // bookmarks.
    //
    // Extracted so the charged and deferred paths cannot drift: a batched
    // checkout is a completed order from the shopper's point of view and must
    // land on the same confirmation, with the same summary.
    navigateToConfirmation(orderId, summary = {}) {
      const slug = resolveStorefrontSlug(this.$route);
      // The PATH keeps the DB id: it is what the confirmation page's
      // fetchOrder() and the backend route are keyed on. Only the DISPLAYED
      // number is the store-formatted one.
      const path = slug
        ? `/shop/${slug}/orders/${orderId}/confirmation`
        : "/order_confirmation";
      this.$router.push({
        path,
        query: {
          // Falls back to the id when the backend left the order unnumbered
          // (order_number is nullable), so the shopper always sees
          // something to quote to support.
          orderNumber: this.orderNumber || orderId,
          itemCount: summary.itemCount > 0 ? summary.itemCount : undefined,
          total: summary.total || undefined,
          allowance: summary.allowance || undefined,
          charged: summary.charged || undefined,
          // Highest-priced item free. Carried so the confirmation page can
          // EXPLAIN the gap between `total` (pre-discount sub-total) and
          // `charged`; without it the shopper sees both numbers and nothing
          // accounting for the difference. Already dollars -- `freeItemDiscount`
          // is `unitCents / 100`. `undefined` keeps the param out of the URL
          // for the stores that do not run the rule, which is all but one.
          freeItemDiscount: summary.freeItemDiscount || undefined,
          // Deferred (batched) checkout: nothing has been charged yet, so the
          // confirmation page must not call `charged` an amount charged.
          deferred: summary.deferred ? "1" : undefined,
        },
      });
    },

    handlePaymentFailed(error) {
      // Error already displayed by CartCheckout via store.dispatch("raiseError", ...)
      // The dialog remains open for retry.
      //
      // A failed payment step (a decline, a card that would not save) settles
      // the checkout attempt: the next Place Order is a new attempt, as it
      // always was. NOT when the attempt is unconfirmed (its answer was lost
      // and the server could not be reached, Bug Intake #637): the order may
      // exist, so the next Place Order must carry the SAME key and get it back.
      if (!this.orderUnconfirmed) settleCheckoutAttempt(this);
    },

    // ── Data loading ──────────────────────────────────────────────────

    async loadCart() {
      console.log("loading cart");
      const cartItems = [];
      // Sequenced: quantity steps re-run this, and an older load can finish
      // after a newer one. Only the newest writes the cart and its price
      // state, so a stale response (or stale failure) cannot win.
      const seq = (this.loadCartSeq || 0) + 1;
      this.loadCartSeq = seq;

      // WHICH store: the gate's id, the ONE answer every cart operation on
      // this page uses (router/storefrontStoreId.js) -- never a second id
      // read off the loaded campaign. Still null until the store has loaded.
      //
      // NO STORE ID, NO CART REQUEST (per-store carts, spec section 8.1).
      // Carts are keyed by (account, store); there is no account-wide cart
      // to show, and guessing the store (localStorage, a line's own store)
      // is exactly how a page once rendered another store's item. A page
      // whose store never resolved asks for nothing and renders nothing.
      const viewingStoreId = this.campaign
        ? normalizeStoreId(unref(this.gateStoreId))
        : null;
      if (!this.$store.state.account || viewingStoreId == null) {
        // Nothing to price: settle, so the loading placeholder never spins.
        clearCartLines(this, null);
        this.linePricesState = "loaded";
        this.pricesLoadedOnce = true;
        return cartItems;
      }

      // The lines held are another store's (or none was read yet): drop
      // them BEFORE the read. If it fails, the error state shows over an
      // empty cart, never over another store's lines. A failed re-read of
      // the SAME store keeps its lines -- they are still this store's.
      if (this.cartStoreId !== viewingStoreId) {
        clearCartLines(this, viewingStoreId);
      }

      // THIS store's cart, as the server has it (`store_cart`). The server
      // returns that store's lines and nothing else, so nothing is filtered
      // here; a line's position is its position in this store's cart.
      let storeLines;
      try {
        storeLines = await fetchStoreCart(
          this.$axios,
          this.$store.state.account._jv.id,
          viewingStoreId
        );
      } catch (err) {
        console.warn("Cart: could not load this store's cart.", describeError(err));
        if (seq === this.loadCartSeq) {
          // The error state (with Retry), never an empty cart: "could not
          // read" must not look like "nothing in it".
          this.linePricesError = cartPricesErrorMessage(err);
          this.linePricesState = "error";
          this.pricesLoadedOnce = false;
        }
        return undefined;
      }
      if (seq !== this.loadCartSeq) return undefined;
      this.$store.commit("setCart", storeLines);

      // PR-N1: fetch all CampaignProducts first, then run a single
      // resolver batch, then assemble cart rows. Splitting the loop
      // keeps the resolver round-trip O(1) per cart load regardless
      // of cart size.
      const fetched = [];
      for (const [cartIndex, cartItem] of storeLines.entries()) {
        let campaignProduct;
        try {
          const result = await this.$axios.get(
            `/campaign_product/getCampaignProductById/${cartItem.campaign_product_id}`
          );
          campaignProduct = result.data;
        } catch (err) {
          // A product deleted since it was carted (or otherwise not
          // loadable) skips its row; the remaining rows still render.
          console.warn(
            `Cart: skipping campaign product ${cartItem.campaign_product_id} ` +
              "-- it could not be loaded.",
            describeError(err)
          );
          continue;
        }
        campaignProduct._jv = { id: cartItem.campaign_product_id };
        fetched.push({ cartIndex, cartItem, campaignProduct });
      }

      // P6 PR-A: each ``decoration_records[*].id`` is a placement_id.
      const pairsSeen = new Set();
      const pairs = [];
      for (const { cartItem, campaignProduct } of fetched) {
        if (!Array.isArray(campaignProduct.decoration_records)) {
          continue;
        }
        for (const decoration of campaignProduct.decoration_records) {
          if (!decoration || decoration.id == null) {
            continue;
          }
          const key = `${decoration.id}|${cartItem.primary_attribute_value}`;
          if (pairsSeen.has(key)) {
            continue;
          }
          pairsSeen.add(key);
          pairs.push({
            placement_id: decoration.id,
            color_sku: cartItem.primary_attribute_value,
          });
        }
      }
      const { resolveBatch } = useResolveBatch();
      const resolverResults = await resolveBatch(pairs);

      // Every line is priced by the SERVER, at the line's real quantity, from
      // the read-only `cart_prices` RPC (2026-09-29). There is deliberately
      // NO fallback -- not the product's qty-1 `variant_prices`, not
      // `default_sale_price`: either would be a second price source, and it
      // surfaces only as a checkout 409 once a line reaches a quantity
      // break. A line the server did not price keeps `price: null`, the
      // cart shows an error with Retry, and checkout stays disabled.
      const linePrices = fetched.length
        ? await this.loadLinePrices(seq, viewingStoreId)
        : null;
      if (seq !== this.loadCartSeq) return undefined;

      for (const { cartIndex, cartItem, campaignProduct } of fetched) {
        const unitCents = lineUnitCents(linePrices, cartItem);
        const price = unitCents === undefined ? null : unitCents / 100;
        const total =
          unitCents === undefined ? null : (unitCents * cartItem.quantity) / 100;

        // Product-image picker slice 1: prefer the builder's per-color
        // ``storefront_images`` primary image, fall back to legacy
        // ``images[color]``. Same single-source fallback as the card.
        const image =
          resolvePrimaryColorImageRef(
            campaignProduct,
            cartItem.primary_attribute_value
          ) ||
          // Bug Intake #460: a product with nothing chosen and no legacy
          // image shows its derived listing photo (the DEFAULT color's, the
          // one the grid and product page show), not the placeholder. It is
          // not the ordered color's photo: the server derives one per product.
          campaignProduct.derived_listing_image ||
          null;
        // PR-N1: decoration label appears IFF the resolver returned
        // hint=ok for the pair. Tested in
        // utils/__tests__/decorationLabels.spec.js.
        const decorations = buildCartDecorations(
          campaignProduct,
          cartItem.primary_attribute_value,
          resolverResults
        );
        cartItems.push({
          campaign_product_id: cartItem.campaign_product_id,
          // ``index`` is the position in THIS store's cart (row testids,
          // quantity edits); ``line_id`` is the line's stable server id,
          // which the Artifi preview (CartItemList) addresses it by.
          index: cartIndex,
          line_id: cartItem.line_id ?? null,
          campaignProduct: campaignProduct,
          product_name: campaignProduct.product_name,
          primary_attribute_value: cartItem.primary_attribute_value,
          secondary_attribute_value: cartItem.secondary_attribute_value,
          decorations: decorations,
          image: image,
          sku: campaignProduct.sku,
          price: price,
          quantity: cartItem.quantity,
          total: total,
          // Artifi PR 6b: forward the personalization shape (PR 6a's
          // list-of-strings on legacy storefront entries, PR 3's
          // dict on Artifi-handoff entries) so the cart UI can show
          // each shopper the per-garment values they captured.
          // ``personalization_fields`` arrives from
          // getCampaignProductById (PR 6a addition) so the labels
          // are stable across renders.
          personalization_values:
            cartItem.personalization_values,
          personalization_fields:
            campaignProduct.personalization_fields,
          // The shopper's LOGO choice, and the names it resolves
          // against. Two sources, deliberately:
          //
          //   * ``selected_artwork_id`` comes off the stored cart line
          //     -- it is the shopper's own pick, recorded at add-to-cart
          //     and part of this line's identity (two lines differing
          //     only by logo are correctly distinct lines). Copied
          //     forward explicitly, like every other per-line field on
          //     this row; without this the cart cannot tell them apart.
          //   * ``logo_names`` comes off the campaign-product payload,
          //     resolved server-side through the same label rule the
          //     PDP picker uses, so the cart caption and the picker
          //     cannot disagree.
          //
          // ``?? null`` because add-to-cart OMITS the key (rather than
          // sending null) for a product with no logos, and the row
          // should carry one falsy shape rather than two.
          selected_artwork_id: cartItem.selected_artwork_id ?? null,
          logo_names: campaignProduct.logo_names || {},
          // Logos offered per color, for the single-logo display rule
          // (CartItemList `logoName`, `utils/loneLogo`).
          logo_counts_by_color: campaignProduct.logo_counts_by_color || {},
        });
      }
      // The page's rows: this store's lines, resolved for display. Vuex
      // holds the same store's lines as stored (`setCart` above).
      this.cart = cartItems;
      // The cart as the server has it now: any hold for a placed order's
      // spent shipping quote is over.
      this.serverQuoteHeld = false;
      // Only lines with a positive quantity need a price: the server skips
      // quantity <= 0 lines.
      this.linePricesState = cartItems.every(
        (row) => row.price !== null || !(Number(row.quantity) > 0)
      )
        ? "loaded"
        : "error";
      // "loaded" makes these the totals to keep on screen during the next
      // re-price; "error" clears that, since the rows hold no prices.
      this.pricesLoadedOnce = this.linePricesState === "loaded";
    },

    /**
     * Fetch the server's per-line prices for this store's cart lines.
     *
     * `cart_prices` is a READ-ONLY Account RPC returning the same `prices`
     * array `update_cart_quantity` returns: one entry per priced line, keyed
     * by (campaign_product_id, color, size, artwork id), with
     * `unit_price_cents` resolved at the line's real quantity.
     *
     * Returns the lookup, or null on ANY failure (no account, network error,
     * a response without a `prices` array). Null leaves every line unpriced,
     * which is what puts the cart in its visible error state; it is never
     * papered over with a locally computed price.
     *
     * `seq` is the calling loadCart's id: a failure from a load that has
     * since been superseded leaves no error message behind.
     */
    async loadLinePrices(seq, storeId = null) {
      this.linePricesState = "loading";
      this.linePricesError = null;
      try {
        // Priced for the store whose lines this page shows -- named, so a
        // privileged account's linked store (whichever it last added to)
        // never decides which lines get prices.
        return await fetchCartLinePrices(
          this.$axios,
          this.$store.state.account,
          storeId
        );
      } catch (err) {
        console.warn("Cart: could not load prices for this cart.", describeError(err));
        if (seq === undefined || seq === this.loadCartSeq) {
          this.linePricesError = cartPricesErrorMessage(err);
        }
        return null;
      }
    },

    retryLinePrices() {
      return this.loadCart();
    },

    /**
     * Load the platform fee context for THIS store.
     *
     * Read by slug because both values are per-store: the rate resolves
     * through the distributor's plan (an override of 0 is a real zero, not
     * "unset") and the bearer sits on the store row. The global
     * /platform-settings rate is NOT a substitute -- using it is what made
     * the cart disagree with the server and 409 at checkout.
     *
     * A failure is recorded, never papered over with a default rate: an
     * invented rate is wrong in both directions and turns a transient read
     * error into a checkout the shopper cannot complete. Sets
     * `platformFeeUnavailable`, which blocks order placement in
     * `calculateOrderTotals`.
     */
    async reloadPlatformFee() {
      try {
        const platformFee = await this.getPlatformFeeRateFromService(
          resolveStorefrontSlug(this.$route)
        );
        this.platformFeeRate = platformFee.rate;
        this.platformFeeBearer = platformFee.bearer;
        this.platformFeeUnavailable = false;
      } catch (err) {
        console.error("platform fee rate unavailable", describeError(err));
        this.platformFeeRate = null;
        this.platformFeeUnavailable = true;
      }
    },

    async loadData() {
      const result = await this.$axios.get(`/stripe/getStripeAccountId`);

      // WHICH STRIPE PLATFORM THIS STORE TOKENIZES ON.
      //
      // Resolved from the SAME payload that carries the connected account,
      // because the two are one fact server-side (`stripe_binding` is
      // `(account_id, mode)`) and asking separately would open a window in
      // which they could disagree.
      //
      // Assigned here rather than initialized in `data()`: `data()` runs ONCE
      // at component creation, long before this payload lands, so an
      // initializer would capture the build-time value and never see the
      // resolved one -- a green test and a broken checkout. The field stays
      // in `data()` holding the build-time constant so it is reactive and so
      // the fallback is already in place if this request fails.
      this.stripePublishableKey = resolvePublishableKey(result.data);

      // Keyed on the store this cart LOADED. A cart whose store did not load
      // has nothing to ask about, and asking with an unresolved id requested
      // `getAppFeeByCampaignAccount/null` (#4092 review). `appFeeMultiplier`
      // keeps its zero default; everything below that depends on the store
      // is skipped by the `!this.campaign` guard further down.
      const appFeeStoreId = this.loadedCampaignId();
      if (appFeeStoreId) {
        this.appFeeMultiplier =
          await this.getAppFeeMultiplierFromService(appFeeStoreId);
      }

      await this.reloadPlatformFee();

      this.getCountries();

      // The campaign may legitimately be UNRESOLVED here, and dereferencing it
      // was a crash that reached the shopper as a generic error toast.
      //
      // `mounted()` gates on `state.account`, NOT on the campaign:
      //
      //     await this.loadCampaign();
      //     if (this.$store.state.account) {
      //       this.loadData();     // <-- not awaited
      //
      // and `loadCampaign()` has a real early-return path -- `waitForCampaignId`
      // times out, warns, and returns with `this.campaign` still at its initial
      // `null`. A PRIVILEGED session satisfies both conditions at once:
      // `StorefrontSessionGate.hydratePrivilegedAccount()` populates
      // `state.account` while deliberately NOT writing `localStorage.campaignId`
      // (writing it would feed `isPrivilegedSession()` and re-open the cross-tab
      // logout bug), so a back-office user opening a storefront cart reached
      // `this.campaign.payment_method` on null and threw.
      //
      // Because `loadData()` is not awaited, that TypeError became an UNHANDLED
      // REJECTION, which `main.js` routes to `raiseError`. A bare TypeError has
      // no `.response`, so `raiseError` fell through to GENERIC_ERROR_MESSAGE:
      // "An error occurred when making your request. Please refresh and try
      // again" -- with every network request still 200, because the request was
      // never made. That is the production symptom, and it tracked the HOST only
      // because `localStorage` (and therefore the privileged session) is
      // origin-scoped.
      //
      // WHY RETURN RATHER THAN DEFAULT. Everything below this line is derived
      // from the campaign, and a campaign we could not load tells us nothing
      // about its payment method. Falling into the `else` would assert
      // `isPayroll = true` -- a GUESS about money that renders payroll checkout
      // copy on a card-payment store. The fee/country work above has already
      // run and does not depend on the campaign, so it is deliberately left
      // outside this guard.
      //
      // This is NOT a catch. Genuine failures on the calls above still reject
      // and still surface a useful message; only the null dereference is
      // prevented. `loadCampaign` has already warned about the unresolved
      // campaign, and `loadCart` fails closed to an empty cart for the same
      // state (cart-fails-closed-without-store-identity.spec.js).
      if (!this.campaign) {
        return;
      }

      if (this.campaign.payment_method == "Individual CC Payment") {
        if (result.data && result.data.stripe_account_id) {
          this.stripe = await loadStripe(this.stripePublishableKey, {
            stripeAccount: result.data.stripe_account_id,
          });
        }
        // Loaded unconditionally, and deliberately NOT gated on the connected
        // account being present: the SetupIntent lives on the platform, so
        // vaulting a card must keep working for a store whose distributor has
        // no connected account resolved yet. (Such a store cannot charge, but
        // that failure belongs to the charge path.)
        //
        // BOTH instances get the resolved key. `setupStripe` is what vaults
        // cards for BATCHED orders, so keying only the connected one would
        // leave every batched order vaulting against the wrong platform --
        // failing days later at batch release rather than at checkout.
        this.setupStripe = await loadStripe(this.stripePublishableKey);
        this.isPayroll = false;
        this.isCard = true;
      } else {
        // NOT "else = payroll". A store with "Accept Shopper Payments" OFF
        // (empty payment_method) is neither card nor payroll -- checkout
        // refuses it outright (PAYMENTS_OFF_MESSAGE) -- so treating it as
        // payroll here would render payroll-deduction copy (CartCheckout's
        // allowance notice) on a store that cannot take the order at all.
        this.isPayroll = isPayrollPaymentMethod(this.campaign.payment_method);
        this.isCard = false;
      }

      // Stripe element initialization is now handled by CartCheckout
      // when the payment dialog opens (via its ccDialog watcher).

      if (this.campaign.is_prevent_order_duplication) {
        this.isCartProcessingFeeTBD = true;
      }
    },

    /**
     * The store id this cart is for, or null when it is not usable yet.
     *
     * ONLY the session gate's id, resolved from this page's URL slug and
     * provided to the pages it wraps. NOT `localStorage.campaignId`: that is
     * the logged-in ACCOUNT's own store, which for a back-office user or an
     * impersonation is an unrelated store -- the cart asked for it and got a
     * 403 (prod, 2026-10-07). Normalized so the literal strings "null" /
     * "undefined" count as "not resolved" (router/storefrontStoreId.js).
     */
    resolvedCampaignId() {
      return normalizeStoreId(unref(this.gateStoreId));
    },

    /**
     * The id of the store this cart actually LOADED, or null before (or
     * without) a successful load. Distinct from `resolvedCampaignId`, which
     * is the gate's answer and may be set while the load failed.
     */
    loadedCampaignId() {
      return this.campaign && this.campaign._jv
        ? normalizeStoreId(this.campaign._jv.id)
        : null;
    },

    /**
     * Wait for the store id to be resolved.
     *
     * WHY WAIT RATHER THAN SKIP. The obvious repair -- return early when the
     * key is missing -- makes the console error go away by rendering an EMPTY
     * CART to a shopper who has items. That is worse than a failed load,
     * because it looks like success, and it is the exact regression card
     * FSPLvtQK already fixed once on this route (`if (state.account)
     * loadCart()` early-returning into an empty cart).
     *
     * The wait is BOUNDED. A cart that waits forever is the same stuck
     * "Loading Cart..." spinner the bug produced, reached by a nicer route.
     * On timeout the caller surfaces the failure instead of pretending the
     * cart is empty.
     *
     * Under the gate the id is already set when the cart mounts (the gate
     * renders its pages only once it is 'ready'), so this returns at once.
     * The bounded wait remains so a cart mounted without a resolved store
     * gives up legibly instead of spinning (cart-cold-load-campaign-id.spec).
     */
    async waitForCampaignId({ timeoutMs = 5000, intervalMs = 25 } = {}) {
      const deadline = Date.now() + timeoutMs;
      for (;;) {
        const id = this.resolvedCampaignId();
        if (id) return id;
        if (Date.now() >= deadline) return null;
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    },

    async loadCampaign() {
      this.fetchingProducts = true;
      let campaign;
      this.$store.commit("setProductsLoaded", false);

      // `mounted()` calls this UNCONDITIONALLY, before it checks
      // `state.account`. History: the id used to come from
      // `localStorage.campaignId`, whose one writer is the async `login`
      // action, so on a COLD load the cart could get here first and
      // interpolate an unset key into the URL. (It now comes from the session
      // gate -- see resolvedCampaignId -- but an unresolved id must still
      // never reach the URL.) Verified
      // against production: `getCampaignById/undefined` and
      // `getCampaignById/null` both return HTTP 500, and `loadCampaign` has
      // no catch, so the rejection left `fetchingProducts` true and the
      // shopper on "Loading Cart..." forever. A reload worked because the key
      // was already present -- exactly the fix-on-reload symptom reported.
      const campaignId = await this.waitForCampaignId();
      if (!campaignId) {
        // Deliberately NOT a silent empty cart -- see waitForCampaignId.
        // eslint-disable-next-line no-console
        console.warn(
          "Cart: no campaign id after waiting for the account bootstrap; " +
            "not requesting a campaign. The cart cannot render reliably " +
            "until the session is established."
        );
        this.storeUnavailable = true;
        this.fetchingProducts = false;
        this.$store.commit("setProductsLoaded", true);
        return;
      }

      // A failed read must END the spinner: without the catch/finally a
      // rejected request left `fetchingProducts` true and the shopper on
      // "Loading Cart..." forever (the same symptom the unresolved-id path
      // above was fixed for). It surfaces as an explicit unavailable state.
      try {
        // The STOREFRONT store read, not the store editor's loader
        // (`/campaign/getCampaignById`). The editor loader is tenant checked,
        // so a back-office user on another distributor's storefront got 403
        // here, and it handed every shopper the editor payload -- internal
        // pricing inputs and every coupon code. This read is open to anyone
        // who may view the storefront and carries only what the cart reads
        // (api/src/endpoints/storefront_store.py).
        const result = await this.$axios.get(storefrontStoreUrl(campaignId));

        campaign = result.data;

        campaign.locations = Object.values(campaign.locations);

        // Take a deep snapshot of the campaign for the staleness guard.
        // Compared on Place Order against a fresh storefront store read.
        this.campaignSnapshot = JSON.parse(JSON.stringify(campaign));

        this._applyCampaignToState(campaign);
        this.storeUnavailable = false;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Cart: the store could not be loaded.", describeError(err));
        this.storeUnavailable = true;
      } finally {
        this.fetchingProducts = false;
        this.$store.commit("setProductsLoaded", true);
      }
    },

    /**
     * Applies a campaign object to the component's derived state.
     *
     * Single source of truth for "campaign → component state"
     * mapping, called by both initial load (loadCampaign) and the
     * staleness guard's mid-checkout refresh (applyFreshCampaign).
     * Extracting this helper eliminates the drift that had
     * accumulated between the two call sites (gate review P1 #4):
     * applyFreshCampaign was missing setShipToShopperLocation,
     * the headers rebuild, the conditional shipping-cost call, and
     * the single-location auto-select.
     *
     * Mirrors every assignment that loadCampaign() previously did
     * inline. New campaign-derived state should be added here, NOT
     * in either caller.
     */
    _applyCampaignToState(campaign) {
      this.campaign = campaign;
      this.checkoutCustomDropdowns = JSON.parse(
        JSON.stringify(this.campaign.dropdown_fields || [])
      );
      if (
        this.campaign.checkout_fields.includes("Location") &&
        this.campaign.locations.length == 1 &&
        !this.shipToShopperAddress
      ) {
        this.checkoutFieldsResult.selectedLocation = this.campaign.locations[0];
      }
      if (campaign.storefront_pricing) {
        this.headers = [
          { text: "", align: "center", sortable: false },
          { text: "Name", value: "product_name" },
          { text: "Color", value: "primary_attribute_value" },
          { text: "Size", value: "secondary_attribute_value" },
          { text: "Decorations", value: "decorations" },
          { text: "Price", value: "price" },
          { text: "Quantity", value: "quantity", align: "center" },
          { text: "Total", value: "total" },
          { text: "", value: "", align: "right", sortable: false },
        ];
      } else {
        this.headers = [
          { text: "", align: "center", sortable: false },
          { text: "Name", value: "product_name" },
          { text: "Color", value: "primary_attribute_value" },
          { text: "Size", value: "secondary_attribute_value" },
          { text: "Decorations", value: "decorations" },
          { text: "Quantity", value: "quantity", align: "center" },
          { text: "", value: "", align: "right", sortable: false },
        ];
      }
      this.collectSalesTax = this.campaign.collect_sales_tax;
      this.collectProcessingFee = this.campaign.collect_processing_fee;
      this.consolidateTaxFee = this.campaign.consolidate_tax_fee;
      this.salesTax = this.campaign.sales_tax
        ? this.campaign.sales_tax
        : "flat-rate";
      if (this.salesTax != "tax-rate") {
        this.flatRate = this.campaign.flat_rate ? this.campaign.flat_rate : 0;
      } else {
        this.flatRate = 0;
      }

      // The EFFECTIVE amount: a disabled store folds in nothing, whatever the
      // column still holds. The server's checkout subtotal uses the same rule
      // (pricing/cart_subtotal.py effective_store_fundraising) and refuses a
      // cart_total that disagrees.
      this.fundraisingAmountPerItem =
        this.campaign.fundraising_enabled &&
        this.campaign.fundraising_amount_per_item
          ? this.campaign.fundraising_amount_per_item
          : 0;

      this.fundraisingType = this.campaign.fundraising_type;

      if (this.campaign.brand_color) {
        this.$store.commit("setBrandColor", this.campaign.brand_color);
      }
      // Allowance v1->v2 (Slice 1): do NOT seed the displayed allowance from
      // the legacy ``campaign.company_allowance`` column. The authoritative
      // draw is computed server-side from v2 at checkout, and the displayed
      // remaining balance is sourced from the v2 ``checkRemainedAllowance``
      // endpoint once the shopper confirms their email (handleEmailConfirmed).
      // Start at 0; the v2 value replaces it. The charge is server-authoritative
      // regardless of this display value.
      this.companyAllowance = 0;
      // Non-duplication-prevention stores have no email-confirm step (the
      // email field is inline), so handleEmailConfirmed never fires for them.
      // Fetch the v2 remaining allowance here so the DISPLAYED charge matches
      // the server-derived charge for those stores too. Fail-safe: returns 0
      // for stores with no active policy / an unauthenticated shopper.
      if (!this.campaign.is_prevent_order_duplication) {
        this.loadV2RemainingAllowance();
      }

      if (
        this.campaign.shipping_cost_enabled &&
        !this.ratesLive
      ) {
        this.calculateShippingCost();
      }
      this.setShipToShopperLocation();
    },

    async loadV2RemainingAllowance() {
      // Allowance v1->v2 (Slice 1): display-only remaining balance from the
      // v2 grant (granted - consumed), NOT the legacy company_allowance
      // column. The charge is server-authoritative; this only keeps the
      // displayed charge consistent with what the server will bill.
      try {
        const response = await this.$axios.post(
          "/order/checkRemainedAllowance",
          { campaign_id: this.campaign._jv.id }
        );
        this.companyAllowance = response.data.remained_allowance || 0;
      } catch (err) {
        this.companyAllowance = 0;
      }
    },

    // ── Shipping & Tax calculations ───────────────────────────────────

    /**
     * Price the shipping line. Every write after an await is gated on this
     * attempt still being the newest (`shippingAttempts`); a superseded
     * attempt resolves to the newest one's outcome.
     */
    calculateShippingCost() {
      const attempt = { id: ((shippingAttempts.get(this) || {}).id || 0) + 1 };
      shippingAttempts.set(this, attempt);
      const isCurrent = () => shippingAttempts.get(this) === attempt;
      attempt.outcome = runShippingAttempt
        .call(this, isCurrent)
        .then((res) =>
          isCurrent() ? res : shippingAttempts.get(this).outcome
        );
      return attempt.outcome;
    },

    /**
     * THE one place a shipping quote is dropped. Everything a quote leaves
     * on the cart goes together -- the figure, the TBD flag, the server
     * quote id and the memo key it was stored under -- so the subtotal, the
     * breakdown request and the checkout payload can never see half of one.
     * Called when a new attempt starts and when the inputs it priced change
     * (the address-entry watcher). `calculatingShippingFailed` is left
     * alone: it says the LAST attempt failed, and the tax line reads it.
     */
    supersedeShippingAttempt() {
      // An attempt still in flight priced the OLD inputs. Making it
      // non-current (the mechanism a new attempt uses) stops it from
      // re-applying that rate, quote id and key while the re-quote is still
      // debounced; whoever awaits it resolves to "no rate".
      const prev = shippingAttempts.get(this);
      if (!prev) return;
      shippingAttempts.set(this, {
        id: prev.id + 1,
        outcome: Promise.resolve(false),
      });
      this.calculatingShipping = false;
    },

    clearShippingQuote() {
      this.shippingCost = 0;
      this.isShippingCostTBD = true;
      this.appliedShippingQuoteKey = null;
      this.shippingQuoteId = null;
    },

    async calculateSmartShipping(isCurrent = () => true) {
      let shippingCost = 0;
      // The ship-from address is resolved by the SERVER from the store
      // (client company -> distributor -> decorator; Live-rate shipping
      // card). The cart names the store and never sends an origin, so a
      // legacy `shipping_cost_info.shipFromAddress` is not read.
      this.calculatingShipping = true;
      // A new attempt: the previous rate's quote id no longer names what
      // the line will show.
      this.shippingQuoteId = null;

      let shipToLocation = await this.getBuyerLocation();
      if (!isCurrent()) {
        return false;
      }

      shipToLocation = this.formatLocation(shipToLocation);
      if (!shipToLocation) {
        // The destination could not be resolved, so nothing was priced.
        // Same rule as above: TBD, not a fabricated $0 (#2882).
        this.calculatingShipping = false;
        this.calculatingShippingFailed = true;
        return false;
      }

      let response;
      const res = true;
      // The request names the store, never an origin; a store naming no
      // carrier / service sends null (checkoutQuoteClient.js).
      const request = liveShippingRequest(this.campaign, {
        location: shipToLocation,
        totalWeight: this.cartTotalWeight,
        userId: this.$store.state.account._jv.id,
        source: "storefront-cart",
      });
      const fetchQuote = async () => {
        const answer = await this.$axios.post(
          "/shipping/getSmartShippingCost",
          request
        );
        // Logged per REQUEST, not per use: one console line per quote the
        // back office was actually asked for.
        logShippingRateProvider(answer, "storefront-cart");
        return answer;
      };
      // Quote ONCE per set of inputs (Jake, 2026-10-22). Continue to
      // Checkout, the AVS answer and the address watcher all land here; a
      // stored quote for the same destination / weight / service answers
      // them without asking again. See checkoutQuoteMemo.js.
      const quoteKey = liveShippingQuoteKey(
        this.campaign,
        shipToLocation,
        this.cartTotalWeight
      );
      try {
        response = quoteKey
          ? await quoteMemoFor(this, "shipping").run(quoteKey, fetchQuote, {
              cacheable: isUsableShippingQuote,
            })
          : await fetchQuote();
      } catch (err) {
        if (!isCurrent()) {
          // Superseded: the shopper has moved on; this failure is moot.
          return false;
        }
        // ONE raiseError per failure (#2882). This dispatched twice, so a
        // single network error produced two identical toasts.
        reportQuoteFailureOnce(this.$store, err, err);
        this.calculatingShipping = false;
        this.calculatingShippingFailed = true;
        return false;
      }
      if (!isCurrent()) {
        // Superseded while the carrier answered: a newer attempt (a
        // corrected zip, a cart edit) owns the line. Applying this answer
        // would show -- and post -- the EARLIER inputs' rate.
        return false;
      }

      // NOT a `finally` any more (#2882). It ran even when the request
      // THREW, where `response` is still undefined -- so it died on
      // `response.data` and masked the real error with a TypeError the
      // shopper could not act on. The catch above now owns the throw path
      // and returns; this only runs when a response actually arrived.
      if (!response || response.data === undefined || response.data === null) {
        this.$store.dispatch(
          "raiseError",
          "Shipping rates are temporarily unavailable. Please try again."
        );
        this.calculatingShipping = false;
        this.calculatingShippingFailed = true;
        return false;
      }

      if (response.data.error) {
        // The backend's explicit "no rate available" envelope. Leave
        // `shippingCost` alone and mark the attempt failed so the cart
        // renders TBD rather than $0.
        if (isNoShipOriginResponse(response)) {
          // The STORE has no shipping origin: retrying cannot help, so no
          // "try again" toast. The persistent inline error names who to
          // contact and Place Order stays blocked.
          this.shipOriginMissingFromQuote = true;
          this.calculatingShipping = false;
          this.calculatingShippingFailed = true;
          return false;
        }
        reportQuoteFailureOnce(this.$store, response.data, response.data.error);
        this.calculatingShipping = false;
        this.calculatingShippingFailed = true;
        return false;
      }

      // A real quote -- INCLUDING a genuine 0, which means the carrier
      // priced this shipment at zero. That is the distinction #2882 is
      // about: 0 from the carrier is a rate, 0 from a failure is not.
      // The body is the TOTAL the shopper pays (carrier rate plus
      // ThreadFlow's surcharge, server-computed and stored under the quote
      // id). The 2021 client-side "+ 0.05" is gone: the server surcharge
      // supersedes it, and checkout charges the stored amount anyway.
      shippingCost = response.data;
      this.shippingCost = shippingCost > 0 ? shippingCost : 0;
      this.shippingQuoteId = readShippingQuoteId(response);
      this.appliedShippingQuoteKey = quoteKey;
      this.calculatingShipping = false;
      return res;
    },

    async getSalesTax() {
      // Claim this lookup. Every write below is gated on the id still being
      // the newest one, so a slow response from an EARLIER edit cannot land
      // on top of a fresher answer -- a shopper correcting a zip produces
      // exactly that race, and the two responses can arrive in either order.
      const requestId = ++this.salesTaxRequestId;
      const isCurrent = () => this.salesTaxRequestId === requestId;

      this.isSalesTaxTBD = false;
      this.salesTaxState = "calculating";
      this.salesTaxLoading = true;

      // A company / department store: the server resolves the destination
      // and taxes it (`checkout_quote`, the same functions the order is
      // built with). No seller lookup, no client-supplied buyer address.
      if (this.storeResolvesDestination) {
        await this.applyStoreDestinationTax(isCurrent);
        return;
      }

      // A ZIP outside the chosen State has no tax to look up (#514). Checked
      // here as well as in the address watcher: Continue to Checkout and the
      // AVS answer call this directly.
      if (this.shipToShopperAddress && this.shopperZipStateRefused) {
        this.refuseSalesTaxForUnmatchedZip();
        return;
      }

      let buyerLocation = await this.getBuyerLocation();
      if (!buyerLocation) {
        if (isCurrent()) {
          // Nothing was looked up, so say so rather than leaving the line
          // asserting a figure it never computed.
          this.isSalesTaxTBD = true;
          this.salesTaxState = "not-calculated";
          this.salesTaxLoading = false;
        }
        return;
      }

      if (
        buyerLocation.country != "US" &&
        buyerLocation.country != "United States"
      ) {
        if (isCurrent()) {
          this.cartSalesTax = 0;
          this.taxInfo = null;
          this.taxRate = 0;
          // US sales tax simply does not apply outside the US. That is a
          // settled answer, not a pending one -- so it reports as
          // "no tax collected", not as "not yet calculated".
          this.salesTaxState = "not-collected";
          this.salesTaxLoading = false;
        }
        return;
      }

      buyerLocation = this.formatLocation(buyerLocation);
      const total = this.total;

      const lookup = async () => {
        // No seller location is sent (card #628). The distributor's address
        // is withheld from storefront callers, so it was always null here and
        // the free-text parse it fed answered "not available" on every
        // checkout. The route resolves nexus from distributor_id server-side
        // and never read it.
        const response = await this.$axios.post("/sales_tax/getSalesTax", {
          total,
          buyerLocation: buyerLocation,
          distributor_id: this.campaign.distributor_id,
          campaign_id: this.campaign._jv.id,
        });
        return response.data[1];
      };
      // Look the tax up ONCE per cart total and destination (Jake,
      // 2026-10-22): the address watcher, Continue to Checkout and the AVS
      // answer all call this, and an unchanged zip / state / country must
      // reuse the stored answer. See checkoutQuoteMemo.js.
      const taxKey = salesTaxQuoteKey({
        storeId: this.campaign._jv.id,
        total,
        location: buyerLocation,
      });

      try {
        const taxObject = taxKey
          ? await quoteMemoFor(this, "sales-tax").run(taxKey, lookup, {
              cacheable: (answer) => Boolean(answer && typeof answer === "object"),
            })
          : await lookup();
        if (!isCurrent()) {
          // A newer lookup has already been issued (and may already have
          // answered). Drop this one on the floor -- including its loading
          // flag, which the newer call owns.
          return;
        }
        this.applySalesTaxResult(taxObject);
      } catch (err) {
        // Once per failure: a lookup shared by two callers rejects both.
        reportQuoteFailureOnce(this.$store, err, err);
        if (!isCurrent()) {
          return;
        }
        this.cartSalesTax = 0;
        this.taxInfo = null;
        this.taxRate = 0;
        // The lookup FAILED. Fall back to the deferred wording rather than
        // showing a fabricated $0.00 -- the same contract #2882 established
        // for the shipping line.
        this.isSalesTaxTBD = true;
        this.salesTaxState = "not-calculated";
      } finally {
        if (isCurrent()) {
          this.salesTaxLoading = false;
        }
      }
    },

    /**
     * Apply one tax lookup's answer (`compute_sales_tax`'s dict) to the
     * tax line. Shared by the client lookup above and the store-resolved
     * destination quote, so both read the answer the same way.
     */
    applySalesTaxResult(taxObject) {
      this.cartSalesTax = taxObject.totalTax;
      this.taxInfo = taxObject;
      this.taxRate = parseFloat(taxObject.tax_rate);
      // Keyed on `tax_collected`, NOT on `has_nexus`.
      //
      // `tax_collected` answers the only question the shopper's line is
      // making a claim about: did we apply a rate? It is false for ALL
      // THREE ways a store ends up collecting nothing -- no nexus in the
      // buyer's state, the store not collecting tax at all, and a
      // tax_exempt company location -- and Jake + Mike settled that those
      // read identically, because each is the owner's configured choice
      // rather than an anomaly to explain.
      //
      // `has_nexus` would be WRONG here. An exempt company location has
      // nexus (has_nexus true) and still collects nothing; keying on nexus
      // would call it "calculated" and render "$0.00", claiming tax was
      // collected when it was not.
      //
      // Absent (an older API, or a cached response) we fall back to
      // "calculated", which is the pre-existing behaviour -- never a WORSE
      // claim than before.
      this.salesTaxState =
        taxObject.tax_collected === false ? "not-collected" : "calculated";
    },

    /**
     * Ask `checkout_quote` (read-only) for THE breakdown of the cart's
     * current inputs and store it against their key. A failure or a late
     * answer for inputs the cart has since left stores nothing: the cart
     * keeps showing its estimate, and checkout still validates and charges
     * the server's own figure. No toast: the estimate is on screen and
     * marked, and the next input change asks again.
     */
    async fetchServerTotals() {
      const args = this.serverQuoteArgs;
      const key = this.serverQuoteKey;
      if (!args || !key) return;
      const requestId = ++this.serverQuoteRequestId;
      let result;
      try {
        result = await requestCheckoutQuote(
          this.$axios,
          args.accountId,
          this.gateStoreId,
          {
            buyer_location: args.location,
            shipping_cost: args.shippingCost,
            shipping_quote_id: args.shippingQuoteId,
            coupon_code: args.couponCode,
            client_department_id: args.departmentId,
            // The verified allowance holder (email-domain store); the
            // server draws for it only when it is this session's address.
            shopper_email: args.holderEmail,
          },
          // A failed quote is handled here (the marked estimate stays on
          // screen), so it must not also raise the global error toast.
          { [SUPPRESS_ERROR_TOAST]: true }
        );
      } catch (err) {
        return;
      }
      if (requestId !== this.serverQuoteRequestId) return;
      if (!result || !isServerTotals(result.totals)) return;
      this.serverQuote = {
        key,
        totals: result.totals,
        cardCharge: Number(result.card_charge) || 0,
      };
    },

    /**
     * Ask the server where a company / department store's order ships, and
     * what it costs in tax there: `checkout_quote`, read-only, computed by
     * the same functions `create_pending_order` builds the order with -- so
     * the destination shown and the tax quoted are the ones the order gets.
     *
     * Sends NO address: the server resolves it (store's chosen department,
     * or the shopper's department pick, then store > company > distributor).
     * Stores the `ship_to` answer on `storeShipTo` and returns the whole
     * quote, or null on failure (`storeShipToState` = "failed"). A newer
     * quote supersedes an older one that answers late.
     */
    async quoteStoreDestination() {
      if (!this.storeResolvesDestination || !this.$store.state.account) {
        return null;
      }
      if (this.storeDestinationAwaitsDepartment) {
        // Supersede any quote still in flight for an earlier pick.
        this.storeShipToRequestId += 1;
        this.storeShipTo = null;
        this.storeShipToState = "awaiting-department";
        return null;
      }
      const requestId = ++this.storeShipToRequestId;
      const isCurrent = () => this.storeShipToRequestId === requestId;
      const pickedDepartmentId =
        this.checkoutFieldsResult.selectedClientDepartmentId || null;
      if (pickedDepartmentId !== this.storeShipToDepartmentId) {
        this.storeShipTo = null;
      }
      this.storeShipToState = "loading";
      let result;
      try {
        // No quote id: on a live-rate store the server answers the
        // destination and its tax, and says a shipping quote is still
        // required (`shipping_quote_required`) rather than pricing one.
        result = await requestCheckoutQuote(
          this.$axios,
          this.$store.state.account._jv.id,
          this.gateStoreId,
          { shipping_cost: 0, client_department_id: pickedDepartmentId }
        );
      } catch (err) {
        if (isCurrent()) {
          // Never leave an earlier answer on screen as if it still held.
          this.storeShipTo = null;
          this.storeShipToState = "failed";
        }
        return null;
      }
      if (!isCurrent()) {
        return null;
      }
      this.storeShipToDepartmentId = pickedDepartmentId;
      this.storeShipTo = (result && result.ship_to) || null;
      this.storeShipToState = this.storeShipTo ? "loaded" : "failed";
      return this.storeShipTo ? result : null;
    },

    /**
     * `getSalesTax` for a company / department store: quote the
     * server-resolved destination and apply its tax. `isCurrent` is the
     * caller's tax-request guard. An unresolved destination, a failed quote
     * or a lookup that returned nothing all leave the line on its deferred
     * wording -- never a fabricated $0.00 (#2882) -- and an unresolved one
     * also blocks the order (`storeDestinationMissing`).
     */
    async applyStoreDestinationTax(isCurrent) {
      const result = await this.quoteStoreDestination();
      if (!isCurrent()) {
        return;
      }
      const taxObject =
        result && result.ship_to && result.ship_to.complete
          ? result.tax_info
          : null;
      if (taxObject) {
        this.applySalesTaxResult(taxObject);
      } else {
        this.cartSalesTax = 0;
        this.taxInfo = null;
        this.taxRate = 0;
        this.isSalesTaxTBD = true;
        this.salesTaxState = "not-calculated";
      }
      this.salesTaxLoading = false;
    },

    /**
     * Fire a sales-tax lookup as soon as the shopper has entered enough of
     * an address to answer the question, debounced so typing does not spam
     * the endpoint.
     *
     * ZIP + STATE is the whole precondition. The rate is zip-driven
     * (`get_tax_rate_from_csv(region, zip_code)`) and nexus matches on STATE
     * (`distributor_has_nexus` compares `get_state_name(region)` against the
     * distributor's `nexus_states`). Name, street and city do not affect the
     * answer, so waiting for them would defer the lookup for nothing.
     *
     * DISPLAY ONLY, and it cannot be gamed: `Account.checkout` recomputes
     * the tax server-side via `compute_sales_tax` and ignores whatever the
     * client sends. This method changes what the shopper SEES before
     * checkout, never what they are charged.
     */
    scheduleSalesTaxRecalculation() {
      if (this.salesTaxDebounceTimer) {
        clearTimeout(this.salesTaxDebounceTimer);
        this.salesTaxDebounceTimer = null;
      }
      if (!this.collectSalesTax || !this.campaign) {
        return;
      }
      this.salesTaxDebounceTimer = setTimeout(() => {
        this.salesTaxDebounceTimer = null;
        this.getSalesTax();
      }, SALES_TAX_DEBOUNCE_MS);
    },

    /**
     * Drop every tax figure and stop every tax request for an address whose
     * ZIP is not in its State (#514): cancel the pending debounce, make any
     * lookup in flight non-current so it cannot land afterwards, and put the
     * tax line back on its deferred wording -- never a fabricated $0.00.
     */
    refuseSalesTaxForUnmatchedZip() {
      if (this.salesTaxDebounceTimer) {
        clearTimeout(this.salesTaxDebounceTimer);
        this.salesTaxDebounceTimer = null;
      }
      this.salesTaxRequestId += 1;
      this.cartSalesTax = 0;
      this.taxInfo = null;
      this.taxRate = 0;
      this.isSalesTaxTBD = true;
      this.salesTaxState = "not-calculated";
      this.salesTaxLoading = false;
    },

    async calculateOrderTotals() {
      this.placingOrder = true;
      // Blocked when the store's platform fee rate could not be read.
      // Same contract as the shipping-rate block below (#2882): without an
      // authoritative rate the grand total is a guess, and the backend
      // re-validates the submitted charge to the cent -- so submitting
      // would earn a 409 the shopper cannot act on. Retry the read once
      // here (the failure is usually transient) and only refuse if it
      // fails again.
      if (this.platformFeeUnavailable) {
        await this.reloadPlatformFee();
      }
      if (this.platformFeeUnavailable) {
        this.placingOrder = false;
        return false;
      }
      if (this.collectSalesTax) {
        await this.getSalesTax();
      }
      let res = true;
      if (this.canCalculateSmartShipping) {
        res = await this.calculateShippingCost();
      } else if (!this.ratesLive) {
        res = await this.calculateShippingCost();
      }
      if (!res) {
        this.placingOrder = false;
        return false;
      }
      // Return an EXPLICIT boolean (#2882). This used to return undefined
      // on both paths, so `placeOrderButtonClick` could not tell a failed
      // shipping recompute from a successful one and submitted regardless
      // -- with shipping_cost 0, on an order the shopper was shown as TBD.
      return true;
    },

    // ── Location helpers ──────────────────────────────────────────────

    formatLocation(location) {
      if (location) {
        const formattedLocation = {};
        formattedLocation.street_and_number = location.street_and_number;
        formattedLocation.city = location.city;
        formattedLocation.zip_code = location.zip_code;
        formattedLocation.region = location.region;
        formattedLocation.country = location.country;
        formattedLocation.suite_unit_etc = location.suite_unit_etc
          ? location.suite_unit_etc
          : "";
        formattedLocation.isCompanyLocation = location.isCompanyLocation;
        return formattedLocation;
      }
    },

    async getBuyerLocation() {
      // A company / department store ships to the SERVER-resolved
      // destination; smart shipping rates against that address. The server
      // ignores any buyer location for these stores' tax.
      if (this.storeResolvesDestination) {
        if (!this.storeShipTo) {
          await this.quoteStoreDestination();
        }
        return shipToQuoteLocation(this.storeShipTo);
      }
      let foundLocation;
      if (this.shipToShopperAddress) {
        foundLocation = this.shopperLocation;
      } else {
        foundLocation = this.checkoutFieldsResult.selectedLocation;
      }
      if (foundLocation) {
        foundLocation.isCompanyLocation = !this.shipToShopperAddress;
      }
      return foundLocation;
    },

    async getParsedAddress(address) {
      let response;
      try {
        response = await this.$axios.post(
          "/address_validation/getParsedAddress",
          {
            location: address,
          }
        );
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
      }

      if (isUnparseableAddress(response.data)) {
        this.$store.dispatch(
          "raiseError",
          unparseableAddressMessage(response.data)
        );
        return null;
      }
      // NOTE (card #628): the free-text parse routes may return
      // `zip_or_state_changed: true` (parseAndValidateAddress under
      // deconetwork). It is NOT stripped here on purpose: no consumer of this
      // return value persists it server-side (ValidateAddressDialog omits it
      // before saving; the ship-to save path never applies a flagged address).
      return response.data;
    },

    async getCountries() {
      let result;
      try {
        result = await this.$axios.get(`/campaign/getCountryCodes`);
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.countriesAndCodes = result.data;
      }
    },

    setShipToShopperLocation() {
      if (
        this.campaign.checkout_fields.includes("Location") &&
        this.campaign.locations.length > 0
      ) {
        this.shipToShopperAddress = false;
      } else if (this.campaign.checkout_fields.includes("Shopper Location")) {
        this.shipToShopperAddress = true;
      }
    },

    // ── Cart operations ───────────────────────────────────────────────

    async keepShopping() {
      await this.updateCart();
      // Convergence (card q7EQ3a0b): "Keep Shopping" returns to the NEW
      // storefront Home (`/shop/<slug>`, StorefrontSessionGate -> HomePage),
      // NOT the legacy single-page `/store/<slug>`. The slug resolver covers
      // `:slug` (new session routes) + `:id` (legacy entry) + localStorage.
      // Phase D (ADR 0059): the no-slug fallback push to bare `/shop` no
      // longer mounts a page directly -- it redirects (router/redirects.js)
      // via the same localStorage.storeUrl lookup resolveStorefrontSlug
      // already tried, so it lands on /login when no store context exists.
      const slug = resolveStorefrontSlug(this.$route);
      this.$router.push(slug ? `/shop/${slug}` : "/shop");
    },

    async updateCart() {
      if (this.saved === true) {
        return;
      }

      for (const cartItem of this.cart) {
        // Artifi PR 6a: preserve personalization_values across the
        // cart re-issue. Without this, a shopper who edits cart
        // quantity in StorefrontCart would silently strip the
        // personalization they captured at add-to-cart time. The
        // backend RPC accepts personalization_values as optional,
        // so legacy non-personalized cart items omit the key.
        const rpcArgs = {
          campaign_product_id: cartItem.campaignProduct._jv.id,
          primary_attribute_value: cartItem.primary_attribute_value,
          secondary_attribute_value: cartItem.secondary_attribute_value,
          quantity: cartItem.quantity,
          prevent_cart_update_on_error: true,
          ...cartStoreArgs(this.gateStoreId),
        };
        if (Array.isArray(cartItem.personalization_values)) {
          // Truncate/extend to match the (possibly updated) quantity
          // so the backend length-vs-quantity validator passes. If
          // the shopper bumped quantity in StorefrontCart, the new
          // tail slots are empty strings; if they dropped quantity,
          // we trim the tail.
          const existing = cartItem.personalization_values;
          const qty = Number(cartItem.quantity) || 0;
          if (existing.length === qty) {
            rpcArgs.personalization_values = existing.slice();
          } else if (existing.length > qty) {
            rpcArgs.personalization_values = existing.slice(0, qty);
          } else {
            rpcArgs.personalization_values = existing.concat(
              Array.from({ length: qty - existing.length }, () => "")
            );
          }
        }
        const response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/update_cart_quantity`,
          {
            meta: {
              method: "update_cart_quantity",
              args: rpcArgs,
            },
          }
        );
      }

      this.saved = true;
    },

    applyFundraisingAmount(number) {
      if (this.fundraisingAmountPerItem) {
        if (this.fundraisingType == "percentage") {
          number = number / ((100 - this.fundraisingAmountPerItem) / 100);
        } else if (this.fundraisingType == "flat_rate") {
          number += this.fundraisingAmountPerItem;
        }
      }

      return this.formatCurrency(number);
    },

    // ── Location change handler ───────────────────────────────────────

    async changedCompanyLocation() {
      if (!this.placeOrderButtonDisabled && this.collectSalesTax) {
        await this.getSalesTax();
      }
      await this.calculateShippingCost();
    },

    // ── Coupon ────────────────────────────────────────────────────────

    async applyCoupon() {
      if (this.coupon) {
        this.coupon = this.coupon.trim();
      }
      // RACE GUARD. Two awaits sit between the click and the discount (the
      // coupon check, then the duplicate-use check). The code and a sequence
      // number are captured NOW; after each await an answer is dropped if a
      // newer Apply started or the shopper changed the code meanwhile, so a
      // slow answer for an old code can never overwrite a newer one.
      // `couponChecking` disables Apply AND Place Order for the duration, so
      // an order cannot be placed against a discount still being decided.
      const code = this.coupon;
      this.couponLookupSeq += 1;
      const seq = this.couponLookupSeq;
      const stale = () => seq !== this.couponLookupSeq || code !== this.coupon;
      this.couponChecking = true;
      try {
        // The server resolves the code (the checkout's own matcher, so a code
        // applied here is one checkout accepts). The cart never holds the
        // store's coupon list.
        const selectedCoupon = await this.lookupCoupon(code);
        if (stale()) return;
        if (selectedCoupon === undefined) {
          // The lookup itself failed (raiseError has said so). Whatever was
          // applied before is no longer known to be valid.
          this.couponAmount = 0;
          return;
        }

        if (selectedCoupon) {
          // check if Coupon code has used in the previous order with same company email
          let response;
          try {
            response = await this.$axios.post(
              "/order/checkPreventOrderDuplicationCoupon",
              {
                campaign_id: this.campaign._jv.id,
                company_email: this.checkoutFieldsResult.companyEmail,
                coupon_code: code,
              }
            );
          } catch (err) {
            if (stale()) return;
            this.couponAmount = 0;
            this.$store.dispatch("raiseError", err);
            return;
          }
          if (stale()) return;
          if (response.data.error === "Coupon is already used") {
            this.couponAmount = 0;
            this.notificationHeader = "Duplicate Coupon Code";
            this.notificationBody =
              "Our records show that you have already used this coupon on a previous order. " +
              "Please note that our system only allows one use of each coupon code per customer. " +
              "Please enter a valid coupon code tor continue to checkout.";
            this.notificationBtn = "Got it!";
            this.coupon = "";
            this.showNotificationDialog = true;
          } else {
            this.couponAmount = selectedCoupon.amount;
            this.couponType = selectedCoupon.discount_type;
            this.notificationHeader = "Congrats!";
            this.notificationBody =
              "The coupon code " + code + " gets you ";
            this.notificationBody +=
              this.couponType === "$"
                ? this.couponType + parseFloat(this.couponAmount).toFixed(2)
                : "";
            this.notificationBody +=
              this.couponType === "%" ? this.couponAmount + this.couponType : "";
            this.notificationBody += " off of your order!";
            this.notificationBtn = "OKAY";
            this.showNotificationDialog = true;
          }
        } else {
          this.couponAmount = 0;
          this.notificationHeader = "Whoops!";
          this.notificationBody = "The coupon you entered is invalid.";
          this.notificationBtn = "OKAY";
          this.showNotificationDialog = true;
        }
        if (this.canCalculateSmartShipping && !this.shipToShopperAddress) {
          this.calculateShippingCost();
        } else if (!this.ratesLive) {
          this.calculateShippingCost();
        }
      } finally {
        // Only the newest lookup owns the flag: an older one finishing must
        // not re-enable Place Order while a newer one is still in flight.
        if (seq === this.couponLookupSeq) {
          this.couponChecking = false;
        }
      }
    },

    /**
     * Ask the server what ONE coupon code gets the shopper on this store.
     *
     * Resolves to `{amount, discount_type}` for an applicable code, `null`
     * for an inapplicable one (unknown, soft-deleted, another store's, or
     * coupons disabled), and `undefined` when the request itself failed
     * (already reported through raiseError). Failed guesses are rate
     * limited server-side, so this is not an enumeration oracle.
     */
    async lookupCoupon(code) {
      const storeId = this.loadedCampaignId();
      if (!storeId || !code) return null;
      let response;
      try {
        response = await this.$axios.post(storefrontCouponUrl(storeId), {
          code,
        });
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return undefined;
      }
      const data = response && response.data;
      if (!data || data.applicable !== true) return null;
      return { amount: Number(data.amount), discount_type: data.discount_type };
    },

    async applyCouponAutomatically() {
      const selectedCoupon = await this.lookupCoupon(this.coupon);
      if (selectedCoupon === undefined) return;
      if (selectedCoupon) {
        this.couponAmount = selectedCoupon.amount;
        this.couponType = selectedCoupon.discount_type;
      } else {
        this.couponAmount = 0;
        this.couponFailedDialog = true;
      }
      if (this.canCalculateSmartShipping && !this.shipToShopperAddress) {
        this.calculateShippingCost();
      } else if (!this.ratesLive) {
        this.calculateShippingCost();
      }
    },

    // ── Checkout staleness guard ─────────────────────────────────────
    //
    // PR #920 relaxed admin-edit gates so admins can edit checkout
    // fields, payment method, etc. while a campaign has live orders
    // and is active. The shopper-side guard below re-fetches the
    // campaign on Place Order, classifies the delta, and either
    // proceeds, silently updates derived fields, or shows a dialog
    // and re-renders the form. Backed by a backend 409 from
    // _validate_checkout_totals (gate review P1 #1).

    /**
     * Shared helper called by BOTH order paths
     * (placeOrderButtonClick AND doCompleteOrderWork — the second
     * path is the one most likely to be missed).
     *
     * Returns true if it's safe to proceed with order placement,
     * false if a staleness dialog has been opened (the user must
     * review and click Place Order again).
     */
    async _runStalenessCheckThenProceed() {
      // In-flight Promise guard (gate review P0 #1):
      // de-dupes concurrent invocations from a double-clicked
      // Place Order button. Without this, two rapid clicks would
      // each kick off their own store-read round-trip and
      // both proceed to checkout, risking a double-charge. Pattern:
      // project_inflight-promise-guard-pattern.md.
      if (this._stalenessCheckInFlight) {
        return this._stalenessCheckInFlight;
      }
      this._stalenessCheckInFlight = (async () => {
        const tier = await this.refreshCampaignForStaleness();
        if (tier === "blocking" || tier === "form-affecting") {
          return false;
        }
        return true;
      })().finally(() => {
        this._stalenessCheckInFlight = null;
      });
      return this._stalenessCheckInFlight;
    },

    /**
     * Re-fetches the campaign and classifies the delta against
     * campaignSnapshot. Side-effects depend on the tier:
     *   none           — nothing
     *   silent         — applyFreshCampaign (quietly update derived fields)
     *   form-affecting — applyFreshCampaign + handleStalenessFormAffecting
     *   blocking       — applyFreshCampaign + handleStalenessBlocking
     *
     * On network failure, logs a warning and returns 'none' — we do
     * not want a transient network blip to block a shopper from
     * checking out. The backend 409 acts as the authoritative final
     * guard.
     */
    async refreshCampaignForStaleness() {
      let fresh;
      try {
        const result = await this.$axios.get(
          storefrontStoreUrl(this.campaign._jv.id)
        );
        fresh = result.data;
        if (fresh && fresh.locations && !Array.isArray(fresh.locations)) {
          fresh.locations = Object.values(fresh.locations);
        }
      } catch (err) {
        // Don't block the shopper on a transient fetch failure;
        // the backend 409 is the authoritative final check.
        console.warn(
          "refreshCampaignForStaleness: failed to fetch campaign, skipping check",
          describeError(err)
        );
        return "none";
      }

      const { tier } = detectCampaignStaleness(this.campaignSnapshot, fresh);
      if (tier === "blocking") {
        this.handleStalenessBlocking(fresh);
      } else if (tier === "form-affecting") {
        this.handleStalenessFormAffecting(fresh);
      } else if (tier === "silent") {
        this.applyFreshCampaign(fresh);
        this.campaignSnapshot = JSON.parse(JSON.stringify(fresh));
      }
      return tier;
    },

    /**
     * Mid-checkout refresh: apply a freshly-fetched campaign to
     * derived state and recompute totals.
     *
     * _applyCampaignToState handles the wholesale state update;
     * per-field copies in data() are derived values kept in sync
     * there. Re-running calculateOrderTotals (gate review P1 #10)
     * ensures cart_charge / cartProcessingFee / sales tax reflect
     * the fresh fees/tax before the next checkout submission.
     */
    applyFreshCampaign(freshCampaign) {
      // _applyCampaignToState handles the wholesale state update;
      // per-field copies in data() are derived values kept in sync
      // there (gate review P2 #13).
      this._applyCampaignToState(freshCampaign);
      // Recompute derived totals so the next checkout submission
      // sends fresh numbers (gate review P1 #10).
      this.calculateOrderTotals();
    },

    /**
     * Blocking tier: payment method changed. Apply the fresh
     * campaign, recompute isPayroll, force the Stripe Elements to
     * unmount and re-mount (via stripeRemountKey), and prompt the
     * shopper to review payment details before retrying.
     */
    handleStalenessBlocking(freshCampaign) {
      this.applyFreshCampaign(freshCampaign);
      // Match the isPayroll/isCard snapshot rule from loadData().
      this.isPayroll = isPayrollPaymentMethod(freshCampaign.payment_method);
      this.isCard = isCardPaymentMethod(freshCampaign.payment_method);
      this.stripeRemountKey += 1;
      this.stalenessDialogTitle = "Payment Method Changed";
      this.stalenessDialogBody =
        "The store admin updated this campaign's payment method. " +
        "Please review your payment details before placing your order.";
      this.showStalenessDialog = true;
      this.campaignSnapshot = JSON.parse(JSON.stringify(freshCampaign));
    },

    /**
     * Form-affecting tier: a watched form field changed. Apply the
     * fresh campaign, prune any checkoutFieldsResult keys that no
     * longer correspond to a checkout field (preserving the
     * special selectedLocation entry), and prompt the shopper.
     */
    handleStalenessFormAffecting(freshCampaign) {
      this.applyFreshCampaign(freshCampaign);
      const newFields = freshCampaign.checkout_fields || [];
      const fieldKeyByName = {
        "First Name": "firstName",
        "Last Name": "lastName",
        "Company Email": "companyEmail",
        Department: "selectedDepartment",
        Manager: "selectedManager",
      };
      // Client Portal v2 (PR C): the typed department pick is driven by the
      // store->client_department mapping, not a checkout_fields name, so
      // always preserve it across a staleness refresh (the picker re-prompts
      // only if the mapping itself changed).
      const allowedKeys = new Set([
        "selectedLocation",
        "selectedClientDepartmentId",
      ]);
      for (const field of newFields) {
        const key = fieldKeyByName[field] || field;
        allowedKeys.add(key);
      }
      for (const key of Object.keys(this.checkoutFieldsResult)) {
        if (!allowedKeys.has(key)) {
          delete this.checkoutFieldsResult[key];
        }
      }
      this.stalenessDialogTitle = "Campaign Updated";
      this.stalenessDialogBody =
        "The store admin updated this campaign. Please review the form " +
        "and any required information before placing your order.";
      this.showStalenessDialog = true;
      this.campaignSnapshot = JSON.parse(JSON.stringify(freshCampaign));
    },

    /**
     * Called from the checkout() catch block when the backend
     * returns HTTP 409 (totals/config changed between price compute
     * and submit). Re-runs the staleness check; if the local
     * snapshot is somehow already in sync (race already reconciled
     * server-side), falls back to a generic dialog.
     */
    async handleStaleness409() {
      const tier = await this.refreshCampaignForStaleness();
      if (tier === "none" || tier === "silent") {
        this.stalenessDialogTitle = "Order Could Not Be Placed";
        this.stalenessDialogBody =
          "Something changed about this campaign while we were processing " +
          "your order. Please review and try again.";
        this.showStalenessDialog = true;
      }
    },

    // ── Order placement ───────────────────────────────────────────────

    async placeOrderButtonClick() {
      // Set isCheckoutProcess synchronously BEFORE any await so a
      // double-click can't fire a second checkout (gate review
      // P0 #1, P1 #5). The Place Order button binds :disabled to
      // this flag in CartCheckout.vue; setting it here disables
      // the button before the staleness round-trip even starts.
      this.isCheckoutProcess = true;
      try {
        // Staleness guard: re-fetch the campaign and bail if the admin
        // edited it mid-checkout. See checkoutStalenessGuard.js and
        // PR #920 gate review P1 #1.
        const safeToProceed = await this._runStalenessCheckThenProceed();
        if (!safeToProceed) return;
        // No "ignore the click while a quote is loading" gate any more
        // (Jake, 2026-10-22). The quote now starts at address entry, so a
        // shopper can easily press Continue while it is in flight; the old
        // gate dropped that click silently. calculateOrderTotals below joins
        // the in-flight quote and tax lookup (checkoutQuoteMemo.js shares one
        // request per input) and waits for them, so nothing is asked twice
        // and nothing proceeds on a figure that has not landed.
        if (this.shipToShopperAddress) {
          // Blocked when totals could not be computed -- today that
          // means no shipping rate (#2882). The shopper is looking at
          // "Shipping Cost: TBD"; submitting would send shipping_cost 0,
          // which the backend now refuses with a 409 anyway. Stopping
          // here gives them the real reason instead of a server error.
          if (!(await this.calculateOrderTotals())) return;
          this.openValidateAddressDialog();
        } else {
          if (!(await this.calculateOrderTotals())) return;
          // Check prevent order duplication
          if (this.campaign.is_prevent_order_duplication) {
            let totalItems = 0;
            this.cart.forEach((orderItem) => {
              totalItems += parseInt(orderItem.quantity);
            });
            let response;
            try {
              response = await this.$axios.post(
                "/order/checkPreventOrderDuplicationCart",
                {
                  campaign_id: this.campaign._jv.id,
                  company_email: this.checkoutFieldsResult.companyEmail,
                  order_items_count: totalItems,
                  total_cart_amount: this.cartCharge,
                  coupon_code: this.coupon,
                }
              );
            } catch (err) {
              this.$store.dispatch('raiseError', err);
              return;
            } finally {
              if (response && response.data && response.data.message !== "success") {
                this.notificationHeader = response.data.notification_title;
                this.notificationBody = response.data.notification_body;
                this.notificationBtn = response.data.button;
                this.showNotificationDialog = true;
                // eslint-disable-next-line no-unsafe-finally
                return;
              }
            }
          }
          this.openPaymentOrPlaceOrder();
        }
      } finally {
        // Re-enable the button on every exit path of THIS click. The
        // review / payment dialog has been opened by now (every checkout
        // ends there, #517), and its own Place Order is guarded by
        // confirmPlaceOrder (non-card) or CartCheckout.payNow (card), so
        // clearing the flag here cannot let an order through unreviewed.
        this.isCheckoutProcess = false;
      }
    },

    async confirmPlaceOrder() {
      // Reached only from the dialog's Place Order (CartCheckout emits
      // ``confirm-order`` when no card is charged). Claim the in-flight flag
      // synchronously, before any await, so a fast double click cannot post
      // two orders; the Place Order button's own :disabled lags a tick.
      if (this.isCheckoutProcess) return;
      this.isCheckoutProcess = true;
      try {
        await this._placeNonCardOrder();
      } finally {
        this.isCheckoutProcess = false;
      }
    },

    async _placeNonCardOrder() {
      // The no-payment-required branch: the same condition under which
      // CartCheckout.confirmPlaceOrder emits ``confirm-order`` instead of
      // calling payNow.
      if (!this.isCard || this.cartCharge <= 0) {
        // Capture order summary before checkout clears the cart
        const itemCount = this.cart
          ? this.cart.reduce((sum, item) => sum + item.quantity, 0)
          : 0;
        const total = this.total != null ? this.total.toFixed(2) : undefined;
        const allowance = this.companyAllowance > 0 ? this.companyAllowance.toFixed(2) : undefined;
        const charged = this.cartCharge > 0 ? this.cartCharge.toFixed(2) : undefined;
        const orderId = await this.checkout();
        // The order was refused (409 / error): the Confirm Order review is
        // still up and would sit over the staleness or error dialog. Close
        // it so the reason is visible; on success checkout() closes it.
        if (!orderId) this.ccDialog = false;
        // Gate review P1 #2: checkout() returns null on 409
        // (staleness) and on other failures. Do NOT navigate to
        // /order_confirmation in those cases — the staleness or
        // error dialog is already on screen for the shopper to act
        // on. Without this guard the user lands on a confirmation
        // page with no order number, masking the failure.
        if (orderId) {
          // Convergence (card q7EQ3a0b): see comment in handlePaymentComplete.
          // Routed through the shared navigateToConfirmation rather than an
          // inline copy of the same push: the duplicate is how this path and
          // the card path independently pushed the DB id as the displayed
          // order number.
          this.navigateToConfirmation(orderId, {
            itemCount,
            total,
            allowance,
            charged,
          });
        }
      } else {
        // Payment flow is handled by CartCheckout.payNow() via the dialog
        // This branch should not be reached from the orchestrator directly
        // when the store takes cards and cartCharge > 0.
      }
    },

    openValidateAddressDialog() {
      // Each verification attempt reports for itself.
      this.addressVerificationUnavailable = false;
      this.$refs.validateAddressDialog.show();
    },

    onAddressVerificationUnavailable() {
      this.addressVerificationUnavailable = true;
    },

    async updateAddressToValidated(validatedAddress) {
      this.shopperLocation.city = validatedAddress.city;
      this.shopperLocation.region = validatedAddress.region;
      this.shopperLocation.country = validatedAddress.country;
      this.shopperLocation.street_and_number =
        validatedAddress.street_and_number;
      this.shopperLocation.zip_code = validatedAddress.zip_code;
      this.shopperLocation.suite_unit_etc = validatedAddress.suite_unit_etc;
      await this.doCompleteOrderWork();
    },

    async doCompleteOrderWork() {
      // Set isCheckoutProcess synchronously BEFORE any await so a
      // double-click can't fire a second checkout (gate review
      // P0 #1, P1 #5).
      this.isCheckoutProcess = true;
      try {
        // Staleness guard: this is the second path that reaches
        // confirmPlaceOrder/ccDialog (via the address-validation dialog).
        // Without this guard a shopper using "Use Original Address" would
        // bypass the staleness check entirely. See PR #920 P1 #1.
        const safeToProceed = await this._runStalenessCheckThenProceed();
        if (!safeToProceed) return;
        this.shopperLocation.nickname = "Shopper Location";
        this.shopperLocation.company_name = "Shopper";
        this.checkoutFieldsResult.selectedLocation = this.shopperLocation;
        // AWAITED, and its answer honored (Jake, 2026-10-22). This used to
        // fire and forget, so the payment dialog opened while the totals
        // were still being re-quoted -- and could post a shipping line still
        // showing TBD. With the stored quotes (checkoutQuoteMemo.js) this
        // asks the network only when AVS changed the zip / state / country;
        // otherwise it answers from the quotes taken at address entry.
        if (!(await this.calculateOrderTotals())) return;
        // Check prevent order duplication
        if (this.campaign.is_prevent_order_duplication) {
          let totalItems = 0;
          this.cart.forEach((orderItem) => {
            totalItems += parseInt(orderItem.quantity);
          });
          let response;
          try {
            response = await this.$axios.post(
              "/order/checkPreventOrderDuplicationCart",
              {
                campaign_id: this.campaign._jv.id,
                company_email: this.checkoutFieldsResult.companyEmail,
                order_items_count: totalItems,
                total_cart_amount: this.cartCharge,
                coupon_code: this.coupon,
              }
            );
          } catch (err) {
            this.$store.dispatch('raiseError', err);
            return;
          } finally {
            if (response && response.data && response.data.message !== "success") {
              this.notificationHeader = response.data.notification_title;
              this.notificationBody = response.data.notification_body;
              this.notificationBtn = response.data.button;
              this.showNotificationDialog = true;
              // eslint-disable-next-line no-unsafe-finally
              return;
            }
          }
        }
        this.openPaymentOrPlaceOrder();
      } finally {
        // Re-enable the button on every exit path. checkout() also
        // resets this flag; the last write wins. See the same
        // comment in placeOrderButtonClick.
        this.isCheckoutProcess = false;
      }
    },

    /**
     * The single end-of-checkout step: open the dialog.
     *
     * Both place-order paths (``placeOrderButtonClick`` and
     * ``doCompleteOrderWork``, the latter reached via the address-validation
     * dialog) end here, and neither may create the order themselves.
     *
     * History worth keeping. This used to decide "card dialog, or create the
     * order now". The card test was once ``storefront_pricing`` (a DISPLAY
     * switch), which let the live AdventHealth store place an unpaid $99.00
     * order (production order #2); it became ``payment_method`` /
     * ``cartCharge > 0``. Bug Intake #517 then removed the "create now"
     * branch: a payroll / invoice order, or one an allowance covers in full,
     * was placed the instant the address step was answered, with no review.
     * The dialog now opens for every store; ``showCCInfo`` in CartCheckout
     * (``isCard && cartCharge > 0``, the same predicate that used to live
     * here) picks the card variant, and CartCheckout.confirmPlaceOrder
     * charges or emits ``confirm-order`` accordingly. Deliberately NOT
     * ``!isPayroll``: a payments-off store is neither card nor payroll, and
     * checkout refuses it server-side when Place Order is pressed.
     */
    openPaymentOrPlaceOrder() {
      // EVERY checkout ends on the dialog (Bug Intake #517). Which variant
      // it renders is CartCheckout's call (`showCCInfo`): the card form and
      // "Review & Enter Payment" when a card is charged, otherwise the
      // card-less "Confirm Order" review. Either way only the dialog's Place
      // Order commits -- an answered address step never creates the order.
      // The card-versus-no-card decision therefore lives in ONE place
      // (`showCCInfo` / CartCheckout.confirmPlaceOrder), not here.
      this.ccDialog = true;
    },

    // Build the checkout_fields dict from the shopper's entered values.
    // Shared by createPendingOrder + checkout so both phases persist the
    // same fields (order pre-create-before-charge, Approach A).
    buildCheckoutFieldsData() {
      const data = Object.assign({}, this.customCheckoutFieldsResult);

      if (this.campaign.checkout_fields.includes("First Name"))
        {data["First Name"] = this.checkoutFieldsResult.firstName;}

      if (this.campaign.checkout_fields.includes("Last Name"))
        {data["Last Name"] = this.checkoutFieldsResult.lastName;}

      if (this.campaign.checkout_fields.includes("Company Email"))
        {data["Company Email"] = this.checkoutFieldsResult.companyEmail;}

      // Client Portal v2 (PR C): department attribution is now a TYPED
      // client_department_id (sent as a top-level RPC arg below), not the
      // legacy free-text checkout_fields["Department"] string. We no longer
      // write data["Department"]; 0/1 stores resolve server-side, N stores
      // send the shopper's pick.

      if (this.campaign.checkout_fields.includes("Location"))
        {data.Location = this.checkoutFieldsResult.selectedLocation.nickname;}

      if (this.campaign.checkout_fields.includes("Shopper Location")) {
        if (this.shipToShopperAddress) {
          data["Shopper Location"] =
            this.checkoutFieldsResult.selectedLocation.street_and_number +
            ", " +
            this.checkoutFieldsResult.selectedLocation.city +
            ", " +
            this.checkoutFieldsResult.selectedLocation.region +
            ", " +
            this.checkoutFieldsResult.selectedLocation.zip_code +
            ", " +
            this.checkoutFieldsResult.selectedLocation.country;
        } else {
          data["Shopper Location"] = "";
        }
      }

      if (this.campaign.checkout_fields.includes("Manager")) {
        const first = this.checkoutFieldsResult.selectedManager.first_name;
        const last = this.checkoutFieldsResult.selectedManager.last_name;

        data.Manager = `${first} ${last}`;
      }
      return data;
    },

    // Build the shared money/args payload for both the create_pending_order
    // and checkout RPCs (identical contract; the finalizer ignores the money
    // for persistence and uses only the frozen order row). ``order_id`` is
    // added by the checkout finalizer caller.
    async buildCheckoutArgs(data) {
      return {
        checkout_fields: data,
        cart_sales_tax:
          this.cartSalesTax > 0 && !this.shippingAddressUnrated
            ? this.cartSalesTax.toFixed(2)
            : 0,
        cart_processing_fee:
          this.cartCharge > 0 ? this.cartProcessingFee.value : 0,
        // A non-card store (payroll, or payments off) takes no card at
        // checkout, so it posts no card charge. `cartCharge` is still
        // nonzero there whenever the allowance does not cover the cart, and
        // the server refuses any nonzero card charge on a store that takes
        // no cards; its totals check expects 0.
        //
        // The figure the shopper SAW (#481): the server breakdown's card
        // charge when the cart is showing one, else the cart's estimate,
        // which the server checks to the cent either way.
        cart_charge: !this.isCard
          ? 0
          : this.serverTotals && this.serverQuote
            ? this.serverQuote.cardCharge
            : this.cartCharge > 0
              ? this.cartCharge
              : 0,
        flat_rate: this.flatRate,
        bigger_than_zero: this.cartCharge > 0,
        sales_tax: this.collectSalesTax ? this.computedSalesTax : 0,
        tax_info: this.collectSalesTax ? this.taxInfo : null,
        coupon_code:
          this.couponAmount && this.couponAmount > 0
            ? this.coupon.trim()
            : null,
        shipping_cost: this.shippingCost > 0 ? this.shippingCost : 0,
        // The server quote behind a live rate. On a live-rate store the
        // server charges what it STORED for this id and ignores
        // shipping_cost; no id there is refused (409).
        shipping_quote_id: this.shippingQuoteId || null,
        // Does the 0 above mean "the carrier quoted zero" or "nothing
        // priced this"? (#2882, gate P0-2.)
        //
        // shipping_cost alone CANNOT say: a failed rate and a genuine $0
        // carrier quote both serialise to 0. The server therefore had no
        // way to refuse the first while allowing the second, which left
        // the original bug open one layer up --
        // a client posting 0 got free shipping.
        //
        // This flag carries that meaning explicitly. False only when the
        // cart is actually SHOWING "TBD" on a store that charges for
        // shipping; a shipping-disabled store has no rate to wait for and
        // must not be blocked (isShippingCostTBD is initialised true).
        shipping_rate_available: this.campaign.shipping_cost_enabled
          ? !this.isShippingCostTBD
          : true,
        // The ship-to address could not be verified (see
        // ``addressVerificationUnavailable``). Only meaningful when shipping
        // to the shopper's own address; the server writes an order note.
        address_unverified: Boolean(
          this.shipToShopperAddress && this.addressVerificationUnavailable
        ),
        // Allowance v1->v2 (Slice 1): these are ADVISORY only. The
        // server derives the authoritative allowance draw from v2 and
        // IGNORES the client-sent used_allowance for the charge. The
        // value here is the v2-sourced remaining balance (from
        // checkRemainedAllowance), not the legacy company_allowance
        // column -- kept for display parity, not trusted by the backend.
        used_allowance: Math.min(this.companyAllowance, this.subTotal),
        cart_total: currency(this.total).value,
        company_allowance: this.companyAllowance,
        // PRE-DISCOUNT on the wire, deliberately. The confirmation email
        // renders its own "Free item: -$X" row BELOW this figure
        // (api/templates/email/order.html:115-118), so sending the
        // already-discounted subtotal would deduct the free item TWICE in the
        // email -- trading the cart-screen arithmetic error Phil reported for
        // an identical one on the receipt. The template's invariant
        // (order.html:107-109): "Reading down the email therefore reproduces
        // the arithmetic that produced Total Charge."
        //
        // This also keeps the client path agreeing with the Stripe-webhook
        // path, which sends the server-derived pre-discount
        // `order.cart_total` (api/src/endpoints/stripe.py:2610).
        sub_total: Math.max(this.subTotal + this.freeItemDiscount, 0),
        app_fee: this.appFee && this.appFee.value ? this.appFee.value : 0,
        // Client Portal v2 (PR C): typed department attribution.
        // null for 0/1-department stores (server auto-resolves);
        // the shopper's pick for N-department stores.
        client_department_id:
          this.checkoutFieldsResult.selectedClientDepartmentId || null,
        // Server-side tax recompute (security): send buyer_location so
        // the backend can compute the authoritative sales_tax amount
        // rather than trusting the client-supplied value.
        buyer_location:
          this.formatLocation(await this.getBuyerLocation()) || null,
      };
    },

    // Phase 1 of the pre-create-before-charge flow (Approach A): create the
    // Order at status payment_pending BEFORE the card is charged, and return
    // its id. Does NOT clear the cart / navigate (the shopper may still bail
    // on the card entry); the checkout() finalizer does that after payment.
    // Returns the new order id, or null on failure (409 staleness routed to
    // the staleness dialog like checkout()).
    async createPendingOrder(options = {}) {
      const data = this.buildCheckoutFieldsData();
      const args = {
        ...(await this.buildCheckoutArgs(data)),
        ...cartStoreArgs(this.gateStoreId),
      };
      // TENDER (Unit 4). Forwarded verbatim when the checkout component
      // supplied one; ABSENT otherwise, which the server reads as the card
      // path -- the same shape every caller that pre-dates this unit sends.
      // Sending an explicit "card" for the default would work too, but
      // leaving it absent keeps the legacy payload byte-identical and means
      // this change cannot alter any existing checkout.
      if (options.tender) {
        args.tender = options.tender;
      }
      // The Net Checkout terms version the shopper accepted (the server's own
      // string, from GET net-pass). Required by the server for a Net tender.
      if (options.netTermsVersion) {
        args.net_terms_version = options.netTermsVersion;
      }
      // ONE KEY PER CHECKOUT ATTEMPT (Bug Intake #637): a retry of this
      // attempt gets the order it already built, never a second one.
      this.orderUnconfirmed = false;
      const attemptKey = attemptKeyForArgs(this, args);
      args.checkout_attempt_key = attemptKey;
      this.isCheckoutProcess = true;
      let response;
      try {
        response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/create_pending_order`,
          { meta: { method: "create_pending_order", args } },
          // The failure is shown here, after an ambiguous one is confirmed.
          { [SUPPRESS_ERROR_TOAST]: true }
        );
      } catch (err) {
        // NO ANSWER (or a 5xx): the order may have been built. Ask before
        // saying anything; a built one is carried on with, not rebuilt.
        if (isAmbiguousFailure(err)) {
          this.isCheckoutProcess = false;
          return resolveLostPendingOrder(this, attemptKey);
        }
        // Every branch below is a definitive refusal: nothing was built, so
        // the attempt is settled and the next Place Order is a new one.
        settleCheckoutAttempt(this);
        // A store-mismatch 409 is not a staleness 409: say what it is.
        const mismatch = cartStoreMismatchMessage(err);
        if (mismatch) {
          this.isCheckoutProcess = false;
          this.$store.dispatch("raiseError", mismatch);
          return null;
        }
        // A Net Checkout refusal (PIN pass gone, terms missing or stale) is
        // answered in place by the checkout dialog: not the staleness
        // dialog (a 409 here is the TERMS, not the store), not an error.
        const netRefusal = netCheckoutRefusalCode(err);
        if (netRefusal) {
          this.isCheckoutProcess = false;
          return { id: null, netRefusal };
        }
        const quoteRefused = shippingQuoteRefusalMessage(err);
        if (quoteRefused) {
          this.isCheckoutProcess = false;
          await handleShippingQuoteRefused(this, quoteRefused);
          return null;
        }
        if (err.response && err.response.status === 409) {
          this.isCheckoutProcess = false;
          await this.handleStaleness409();
          return null;
        }
        this.$store.dispatch("raiseError", err);
        this.isCheckoutProcess = false;
        return null;
      }
      this.isCheckoutProcess = false;
      const result = response.data.meta.result;
      if (!result || !result.id) return null;
      // A REPEAT of an attempt that already went through (its first answer
      // was lost): show that order's confirmation; nothing is paid again.
      if (result.finalized) {
        return showAlreadyPlacedOrder(this, result.id, result.order_number, {
          deferred: !!result.deferred_payment,
        });
      }
      // Returns the SERVER's answer, not just the id: ``deferred_payment``
      // tells CartCheckout whether to SAVE the card (batched -- charged when
      // the batch releases) or CHARGE it (unbatched -- nothing else ever
      // will). The client cannot derive this, because batch-open is fail-open:
      // whether a given order batched is a fact about this request, not a
      // property of the store.
      return { id: result.id, deferred_payment: !!result.deferred_payment };
    },

    // Phase 2 finalizer OR legacy single-shot. When ``orderId`` is passed
    // (the CC path, after the card cleared against the pre-created order),
    // this finalizes that order: transition -> processing + run the deferred
    // side effects. With no ``orderId`` (payroll / zero-charge path) this is
    // the legacy build+finalize in one shot.
    //
    // ``paymentIntentId`` is the payment proof the finalizer requires
    // (https://trello.com/c/nJog08xa). It is only meaningful on the CC path;
    // the legacy path never creates a PaymentIntent and is not gated on one.
    //
    // ``setupIntentId`` is the equivalent proof on the BATCHED path (order
    // batching, https://trello.com/c/JhIG8nVU), where the card is SAVED at
    // checkout and charged at release, so no PaymentIntent exists. The two are
    // NOT interchangeable: the server picks which one it demands from the
    // order's own status, so sending the wrong one is a 402.
    async checkout(orderId = null, paymentIntentId = null, setupIntentId = null) {
      const data = this.buildCheckoutFieldsData();
      const args = {
        ...(await this.buildCheckoutArgs(data)),
        ...cartStoreArgs(this.gateStoreId),
      };
      if (orderId != null) {
        args.order_id = orderId;
      }
      if (paymentIntentId != null) {
        args.payment_intent_id = paymentIntentId;
      }
      if (setupIntentId != null) {
        args.setup_intent_id = setupIntentId;
      }
      // The attempt's key (Bug Intake #637): the finalize carries the key its
      // pending order was built under; a single-shot order carries its own.
      this.orderUnconfirmed = false;
      const attemptKey =
        orderId != null && this.checkoutAttemptKey
          ? this.checkoutAttemptKey
          : attemptKeyForArgs(this, args);
      args.checkout_attempt_key = attemptKey;
      this.isCheckoutProcess = true;
      // This checkout redeems the live rate's shipping quote, after which the
      // server refuses that id (409, by design). Ask no breakdown quote from
      // here on -- not even one already debounced -- until the outcome is
      // known (UI gate, #4712: a quote re-keyed just before Place Order fired
      // after it with the spent id). A debounced request then finds nothing
      // to ask. Released by a failed checkout, or by `loadCart` once a placed
      // order's cart has been re-read.
      this.serverQuoteHeld = true;
      let response;
      try {
        response = await postCheckout(this, args);
      } catch (err) {
        // NO ANSWER (or a 5xx): the order may well have been placed -- the
        // #637 duplicate. Confirm with the server before saying anything,
        // and never clear the shopper's address or cart on the way.
        if (isAmbiguousFailure(err)) {
          const placed = await recoverLostCheckout(this, args, attemptKey);
          this.isCheckoutProcess = false;
          return placed ? applyPlacedOrder(this, placed) : null;
        }
        // A definitive refusal: the attempt is settled.
        settleCheckoutAttempt(this);
        // Not placed: the quote is unspent, so the cart may ask again.
        this.serverQuoteHeld = false;
        // A store-mismatch 409 (the request named a store this order or
        // cart is not for) is not a staleness 409: say what it is.
        const mismatch = cartStoreMismatchMessage(err);
        if (mismatch) {
          this.isCheckoutProcess = false;
          this.$store.dispatch("raiseError", mismatch);
          return null;
        }
        // A quote refusal is answered in place ONLY before any payment was
        // taken (the single-shot payroll / zero-charge path). With a
        // PaymentIntent or a saved card this call runs from
        // handlePaymentComplete, whose orphan-charge handling owns the
        // outcome; "try again" there would invite a second charge (gate
        // #5119 P2-1), so that path is left exactly as it was.
        const quoteRefused =
          paymentIntentId == null && setupIntentId == null
            ? shippingQuoteRefusalMessage(err)
            : null;
        if (quoteRefused) {
          this.isCheckoutProcess = false;
          await handleShippingQuoteRefused(this, quoteRefused);
          return null;
        }
        // Backend staleness guard (PR #920 P1 #1): the
        // _validate_checkout_totals path returns HTTP 409 when the
        // campaign config changed between price compute and submit.
        // Route to handleStaleness409 instead of the generic error
        // path so the shopper sees the staleness dialog and can
        // review before retrying.
        if (err.response && err.response.status === 409) {
          this.isCheckoutProcess = false;
          // Await so the staleness dialog state is committed before
          // returning. The caller (confirmPlaceOrder) inspects the
          // null return value and skips navigation, but the awaited
          // dialog also matters for the handlePaymentComplete path
          // where we need a synchronous state to surface the orphan
          // charge. (Gate review P1 #2.)
          await this.handleStaleness409();
          return null;
        }
        this.$store.dispatch('raiseError', err);
        this.isCheckoutProcess = false;
        return null;
      }
      this.isCheckoutProcess = false;
      return applyPlacedOrder(this, placedOrderOf(response.data.meta.result));
    },


    // ── Validation helpers ────────────────────────────────────────────

    checkItemsCountLimit() {
      if (this.campaign.items_count_limit) {
        let total = 0;

        // This store's lines (the page's view), not the account's whole
        // cart: the limit is per store.
        (this.cart || []).forEach((obj) => (total += obj.quantity));

        if (total > this.campaign.items_count_limit) {
          this.$store.dispatch(
            "raiseError",
            `Total items in cart more than allowed (${this.campaign.items_count_limit})`
          );

          return false;
        }
      }

      return true;
    },

    checkPriceLimit() {
      if (this.campaign.price_limit) {
        if (this.cartTotal > this.campaign.price_limit) {
          this.$store.dispatch(
            "raiseError",
            `Cart total more than allowed (${this.campaign.price_limit})`
          );

          return false;
        }
      }

      return true;
    },

    closeCreditCardDialog() {
      this.ccDialog = false;
    },
  },
};
</script>

<style>
/* The last priced totals, kept on screen while a quantity change re-prices
   the cart (#3884 gate P2-5). Checkout stays disabled meanwhile. */
.cart-summary--repricing {
  opacity: 0.5;
  transition: opacity 0.15s ease-in-out;
}

.total {
  float: right;
}
.v-table__overflow .v-datatable th {
  font-size: 14px;
}

.elevation-1 {
  -webkit-box-shadow: unset !important;
  box-shadow: unset !important;
  border: 0px;
}

@media only screen and (min-width: 1904px) {
  .checkout-margin {
    max-width: 1600px;
  }
}

@media only screen and (min-width: 1264px) and (max-width: 1904px) {
  .checkout-margin {
    max-width: 1350px;
  }
}

@media only screen and (min-width: 960px) and (max-width: 1264px) {
  .checkout-margin {
    max-width: 870px;
  }
}

.text-right {
  text-align: right;
}

.product-spinner {
  z-index: 2;
  position: fixed;
  left: 50%;
  top: 40%;
  transform: translate(-50%, -50%);
}

.shipping-origin-missing {
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.06);
}

.keep-shopping {
  text-decoration: underline;
  cursor: pointer;
  padding: 12px 0;
  margin-bottom: 8px;
}
.panel-header {
  text-transform: uppercase;
  display: flex;
  align-content: center;
  font-weight: 500;
}

/* The white-label mark. Reads as footer text in both renderings -- the
 * linked and unlinked forms must look identical, because whether the
 * mark is a link is a branding consequence, not a visual decision. */
.sf-cart-brand {
  display: inline-flex;
  align-items: center;
}
.sf-cart-brand--link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
</style>
